import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Papa from 'papaparse';
import { auth, db } from '../firebase/config';
import { signOut } from 'firebase/auth';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  setDoc,
  updateDoc
} from 'firebase/firestore';
import schedulesData from '../data/schedules-complete-data';
import { SCHOOL_TERMINAL_GROUPS } from '../data/school-terminal-map';
import DashboardShell from './DashboardShell';
import './AdminDashboard.css';

const SHEET_CSV_URL =
  'https://docs.google.com/spreadsheets/d/e/2PACX-1vSmALqApJN_tNwe_Pp5blp01BQLxoF0z5WZLb0L7kMkhSQB2CtKtyuOe-4Op5Ya7K_OeVZRGZhVxE8N/pub?output=csv';
const STUDENT_MASTER_CSV_URL = SHEET_CSV_URL;

const KPI_TARGET = 90;
const ATTENDANCE_REMINDER_LOOKBACK_DAYS = 14;
const ADMIN_ALLOWED_EMAILS = ['aleyannazifa@gmail.com', 'btopmalaysia@gmail.com'];
const EMPTY_SCHEDULE_FORM = {
  date: '',
  startTime: '',
  endTime: '',
  schoolName: '',
  daerah: '',
  terminal: 'Terminal 1',
  subject: '',
  teacher: '',
  type: 'IVC',
  room: '',
  active: true,
};

const SUBJECT_ACTIVITY_OPTIONS = [
  'BM',
  'BI',
  'SEJ',
  'MATH',
  'MT',
  'SAINS',
  'Play & Talk',
  'Workshop',
  'Seminar',
  'Quiz',
];

const PERFORMANCE_SUBJECTS = ['BM', 'BI', 'MATH', 'SEJARAH'];
const PERFORMANCE_GRADES = ['A', 'B', 'C', 'D', 'E', 'F'];
const PERFORMANCE_SUBJECT_ALIASES = {
  SEJ: 'SEJARAH',
  SEJARAH: 'SEJARAH',
  MT: 'MATH',
  MATEMATIK: 'MATH',
};
const ADMIN_WEEKDAYS = ['Ahad', 'Isnin', 'Selasa', 'Rabu', 'Khamis', 'Jumaat', 'Sabtu'];
const ADMIN_MONTHS = ['Januari', 'Februari', 'Mac', 'April', 'Mei', 'Jun', 'Julai', 'Ogos', 'September', 'Oktober', 'November', 'Disember'];
const NON_SCHOOL_ATTENDANCE_COLUMNS = new Set([
  'Timestamp',
  'Tarikh Sesi',
  'Nama Penuh (Huruf Besar)',
  'Guru mata pelajaran',
  'Terminal',
  'Terminal ',
  'Terminal  ',
  'Sesi',
  'Jumlah Kehadiran Pelajar (cnth: 23/25)',
  'Tahap penguasaan pelajar terhadap topik hari ini',
  'Tahap penguasaan pelajar terhadap topik hari ini ',
  'Tahap penglibatan pelajar dalam kelas hari ini',
  'Tahap penglibatan pelajar dalam kelas hari ini ',
  'Adakah objektif pembelajaran hari ini tercapai?',
  'Adakah objektif pembelajaran hari ini tercapai? ',
  'Cabaran utama semasa kelas dijalankan',
  'Ceritakan secara ringkas perubahan yang diperhatikan dalam diri pelajar (JIKA ADA).',
  'Ceritakan secara ringkas perubahan yang diperhatikan dalam diri pelajar (JIKA ADA). ',
  'Sila muat naik gambar aktiviti bersama pelajar',
  'Jika pelajar tidak hadir: nyatakan nama dan sebab',
  'Column 24',
  'T',
  'PAYMENT',
]);
const EMPTY_PERFORMANCE_FORM = {
  studentId: '',
  subject: 'BM',
  baselineGrade: 'D',
  baselineMark: '',
  currentGrade: 'D',
  currentMark: '',
  examLabel: '',
};

const normalizePersonKey = (value) => String(value || '')
  .normalize('NFKD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/[^a-z0-9]/g, '');

const getCsvField = (row, aliases) => {
  const normalizedAliases = aliases.map(normalizePersonKey);
  const entry = Object.entries(row || {}).find(([key]) => normalizedAliases.includes(normalizePersonKey(key)));
  return String(entry?.[1] ?? '').trim();
};

const MANUAL_SCHOOL_OPTIONS = SCHOOL_TERMINAL_GROUPS.flatMap((group) => (
  group.schools.map((name) => ({
    name,
    daerah: group.daerah,
    terminal: group.terminal,
  }))
));

const DAERAH_BY_SCHOOL = MANUAL_SCHOOL_OPTIONS.reduce((acc, school) => {
  acc[school.name] = school.daerah;
  return acc;
}, {
  'Terminal 1 (SMK Bandar Chiku) (DGM)': 'Gua Musang',
  'Terminal 2 : SMK Tengku Indera Petra 1 (DKK)': 'Gua Musang',
});

const TERMINAL_BY_SCHOOL = MANUAL_SCHOOL_OPTIONS.reduce((acc, school) => {
  acc[school.name] = school.terminal;
  return acc;
}, {
  'SMK Tengku Indera Petra 1': 'Terminal 2',
  'SMK Tengku Indera Petra 2': 'Terminal 2',
  'SMK Tengku Indra Petra': 'Terminal 2',
});

const normalizeSchoolName = (name) => {
  if (!name) return 'Unknown';

  const cleaned = String(name).trim();

  if (cleaned === 'Terminal 1 (SMK Bandar Chiku) (DGM)') return 'SMK Bandar Chiku';
  if (cleaned === 'Terminal 2 : SMK Tengku Indera Petra 1 (DKK)') return 'SMK Tengku Indra Petra';
  if (cleaned === 'SMK Sultan Yahya Petra (1)') return 'SMK Sultan Yahya Petra 1';
  if (cleaned === 'SMK Sultan Yahya Petra (1') return 'SMK Sultan Yahya Petra 1';
  if (cleaned === 'SMK Sultan Yahya Petra 1 ') return 'SMK Sultan Yahya Petra 1';
  if (cleaned === 'SMK Tengku Indera Petra') return 'SMK Tengku Indra Petra';
  if (cleaned === 'SMK Tengku Indra Petra 1') return 'SMK Tengku Indra Petra';
  if (cleaned === 'SMK Tengku Indera Petra 1') return 'SMK Tengku Indra Petra';
  if (cleaned === 'SMK Tengku Indera Petra 2') return 'SMK Tengku Indra Petra 2';
  if (cleaned === 'SMK Tan Sri Muhammad Yaacob') return 'SMK Tan Sri Mohamed Yaacob';

  return cleaned;
};

const getManualSchoolMeta = (schoolName) => {
  const normalizedSchool = normalizeSchoolName(schoolName);
  const manualSchool = MANUAL_SCHOOL_OPTIONS.find((school) => normalizeSchoolName(school.name) === normalizedSchool);

  return {
    schoolName: normalizedSchool,
    daerah: manualSchool?.daerah || DAERAH_BY_SCHOOL[normalizedSchool] || '',
    terminal: normalizeTerminalName(manualSchool?.terminal || TERMINAL_BY_SCHOOL[normalizedSchool] || ''),
  };
};

const escapeCsvValue = (value) => {
  const text = String(value ?? '');
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const isMissingSchoolName = (name) => {
  const value = String(name || '').trim();
  return !value || value === '-' || value === 'Unknown' || value === 'Sekolah belum ditetapkan' || value === 'Sekolah Tidak Diketahui';
};

const normalizeCompareValue = (value) => String(value || '').trim().toLowerCase();

const normalizeNameForMatch = (value) => String(value || '')
  .toUpperCase()
  .replace(/[^A-Z0-9@ ]/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const NAME_STOPWORDS = new Set([
  'BIN',
  'BINTI',
  'BT',
  'B',
  'MOHD',
  'MOHAMAD',
  'MUHAMAD',
  'MUHAMMAD',
  'NUR',
  'NURUL',
  'SITI',
  'WAN',
  'TENGKU',
  'PUTERI',
  'TUAN',
  'NIK',
  'CHE',
  'MD',
  'AB',
  'ABD',
]);

const getNameKeywords = (value) => normalizeNameForMatch(value)
  .split(' ')
  .filter((word) => word.length >= 4 && !NAME_STOPWORDS.has(word));

const textHasNameKeyword = (textWords, keyword) => textWords.some((word) => (
  word === keyword ||
  (word.length >= 5 && keyword.length >= 5 && (word.includes(keyword) || keyword.includes(word)))
));

const isStudentMentionedInText = (text, student) => {
  if (!text || !student?.normalizedName) return false;
  if (text.includes(student.normalizedName)) return true;

  const textWords = text.split(' ').filter(Boolean);
  const keywords = getNameKeywords(student.name);
  if (!keywords.length) return false;

  const matchedKeywords = keywords.filter((keyword) => textHasNameKeyword(textWords, keyword));
  const hasVeryDistinctKeyword = matchedKeywords.some((keyword) => keyword.length >= 7);

  return matchedKeywords.length >= 2 || (hasVeryDistinctKeyword && textWords.length <= 80);
};

const getRowField = (row, labelStart) => {
  const foundKey = Object.keys(row || {}).find((key) => (
    String(key || '').trim().toLowerCase().startsWith(String(labelStart || '').trim().toLowerCase())
  ));

  return foundKey ? row[foundKey] : '';
};

const getFirstRowValue = (row, labels) => {
  const entries = Object.entries(row || {});

  for (const label of labels) {
    const normalizedLabel = String(label || '').trim().toLowerCase();
    const found = entries.find(([key, value]) => (
      String(key || '').trim().toLowerCase() === normalizedLabel &&
      String(value || '').trim()
    ));

    if (found) return String(found[1] || '').trim();
  }

  for (const label of labels) {
    const normalizedLabel = String(label || '').trim().toLowerCase();
    const found = entries.find(([key, value]) => (
      String(key || '').trim().toLowerCase().includes(normalizedLabel) &&
      String(value || '').trim()
    ));

    if (found) return String(found[1] || '').trim();
  }

  return '';
};

const isSchoolAttendanceColumn = (key) => {
  const cleaned = String(key || '').trim();
  if (!cleaned || NON_SCHOOL_ATTENDANCE_COLUMNS.has(cleaned)) return false;

  return /^_*SMK/i.test(cleaned) ||
    /^SMU/i.test(cleaned) ||
    /^Terminal\s+\d/i.test(cleaned);
};

const getSchoolColumnEntries = (row) => Object.entries(row || {})
  .map(([key, value]) => ({
    key,
    schoolName: normalizeSchoolName(String(key || '').replace(/^_+/, '').trim()),
    value: String(value || '').trim(),
  }))
  .filter((entry) => isSchoolAttendanceColumn(entry.key) && entry.value);

const parseAttendanceFraction = (value) => {
  if (!value) return { present: 0, total: 0, percent: 0 };

  const raw = String(value).trim();
  const match = raw.match(/(\d+)\s*\/\s*(\d+)/);

  if (!match) return { present: 0, total: 0, percent: 0 };

  const present = Number(match[1]);
  const total = Number(match[2]);

  if (!total) return { present, total, percent: 0 };

  return {
    present,
    total,
    percent: Math.round((present / total) * 100),
  };
};

const parseLikertScore = (value) => {
  const match = String(value || '').match(/[1-5]/);
  return match ? Number(match[0]) : null;
};

const buildISODate = (year, month, day) => {
  const yyyy = Number(year);
  const mm = Number(month);
  const dd = Number(day);

  if (!yyyy || mm < 1 || mm > 12 || dd < 1 || dd > 31) return '';

  const date = new Date(yyyy, mm - 1, dd);
  if (date.getFullYear() !== yyyy || date.getMonth() !== mm - 1 || date.getDate() !== dd) return '';

  return `${yyyy}-${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}`;
};

const getDateDistanceFromTimestamp = (isoDate, timestampValue) => {
  const timestampDate = parseSheetDate(timestampValue);
  if (!isoDate || !timestampDate) return Number.MAX_SAFE_INTEGER;

  const candidate = new Date(`${isoDate}T00:00:00`).getTime();
  const submitted = new Date(`${timestampDate}T00:00:00`).getTime();
  const dayMs = 24 * 60 * 60 * 1000;
  const diffDays = Math.round((candidate - submitted) / dayMs);

  return Math.abs(diffDays) + (diffDays > 7 ? 1000 : 0);
};

const getSheetTimestampMs = (value) => {
  if (!value) return 0;

  const raw = String(value).trim();
  const match = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/);

  if (match) {
    const [, month, day, year, hour = '0', minute = '0', second = '0'] = match;
    const date = new Date(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute), Number(second));
    return Number.isNaN(date.getTime()) ? 0 : date.getTime();
  }

  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
};

const parseSheetDate = (value, timestampValue = '') => {
  if (!value) return '';

  const raw = String(value).trim();

  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;

  const ddmmyyyy = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+\d{1,2}:\d{2}(?::\d{2})?)?$/);
  if (ddmmyyyy) {
    const [, first, second, yyyy] = ddmmyyyy;
    const firstNumber = Number(first);
    const secondNumber = Number(second);

    if (firstNumber > 12) return buildISODate(yyyy, second, first);
    if (secondNumber > 12) return buildISODate(yyyy, first, second);

    const dayFirst = buildISODate(yyyy, second, first);
    const monthFirst = buildISODate(yyyy, first, second);

    if (!timestampValue || dayFirst === monthFirst) return monthFirst;

    const dayFirstDistance = getDateDistanceFromTimestamp(dayFirst, timestampValue);
    const monthFirstDistance = getDateDistanceFromTimestamp(monthFirst, timestampValue);

    if (dayFirstDistance < monthFirstDistance) {
      return dayFirst;
    }

    return monthFirst;
  }

  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toISOString().slice(0, 10);
  }

  return '';
};

const extractSchoolFromTerminalText = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return 'Unknown';

  const terminalSchoolMatch = raw.match(/terminal\s*\d+\s*\((.+?)\)\s*\([A-Z]+\)/i);
  if (terminalSchoolMatch?.[1]) return normalizeSchoolName(terminalSchoolMatch[1]);

  const bracketMatch = raw.match(/\((SMK[^)]+|SMU\(A\)[^)]+)\)/i);
  if (bracketMatch?.[1]) return normalizeSchoolName(bracketMatch[1]);

  const knownSchools = Object.keys(DAERAH_BY_SCHOOL).sort((a, b) => b.length - a.length);
  const normalizedRaw = raw.toLowerCase();
  const found = knownSchools.find((school) => normalizedRaw.includes(school.toLowerCase()));
  return found ? normalizeSchoolName(found) : 'Unknown';
};

const normalizeSubjectName = (value) => {
  const raw = String(value || '').trim().toLowerCase();
  if (!raw) return 'LAIN';
  if (raw.includes('bahasa melayu') || raw === 'bm' || raw.includes(' bm')) return 'BM';
  if (raw.includes('bahasa inggeris') || raw === 'bi' || raw.includes(' bi')) return 'BI';
  if (raw.includes('matematik') || raw.includes('math') || raw === 'mt') return 'MATH';
  if (raw.includes('sejarah') || raw === 'sej') return 'SEJARAH';
  return 'LAIN';
};

const normalizeSessionName = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return 'Sesi tidak ditetapkan';

  const match = raw.match(/sesi\s*(\d+)/i);
  if (match) return `Sesi ${match[1]}`;

  return raw;
};

const normalizeTerminalName = (value) => {
  const raw = String(value || '').trim();
  const match = raw.match(/terminal\s*(\d+)/i);
  return match ? `Terminal ${match[1]}` : raw;
};

const getTerminalScopeKey = (daerah, terminal) => [
  normalizeCompareValue(daerah),
  normalizeTerminalName(terminal),
].join('|');

const TERMINAL_SCHOOL_BY_SCOPE = SCHOOL_TERMINAL_GROUPS.reduce((acc, group) => {
  acc[getTerminalScopeKey(group.daerah, group.terminal)] = normalizeSchoolName(group.schools[0]);
  return acc;
}, {});

const getTerminalSchoolName = (schedule) => {
  const schoolName = normalizeSchoolName(schedule?.schoolName);
  const daerah = schedule?.daerah || DAERAH_BY_SCHOOL[schoolName] || '';
  const terminal = schedule?.terminal || TERMINAL_BY_SCHOOL[schoolName] || '';
  return TERMINAL_SCHOOL_BY_SCOPE[getTerminalScopeKey(daerah, terminal)] || schoolName;
};

const getSessionSortValue = (value) => {
  const match = String(value || '').match(/(\d+)/);
  return match ? Number(match[1]) : 999;
};

const getAttendanceTone = (percent) => {
  if (percent >= 90) return 'good';
  if (percent >= 75) return 'warn';
  return 'bad';
};

const detectSchoolFromRow = (row) => {
  const terminalSchool = extractSchoolFromTerminalText(row['Terminal'] || row['Terminal '] || row['Terminal  ']);
  if (terminalSchool !== 'Unknown') return terminalSchool;

  return 'Unknown';
};

const inferDaerah = (row, schoolName) => {
  const directDaerah = row['Column 5'] || row['Daerah'] || '';
  if (directDaerah && String(directDaerah).trim()) return String(directDaerah).trim();
  return DAERAH_BY_SCHOOL[schoolName] || 'Unknown';
};

const inferTerminal = (row, schoolName) => {
  const directTerminal = row['Terminal'] || row['Terminal '] || row['Terminal  '] || '';
  if (directTerminal && String(directTerminal).trim()) return String(directTerminal).trim();
  return TERMINAL_BY_SCHOOL[schoolName] || '-';
};

const cleanTeacherName = (value) => {
  if (!value) return '-';
  return String(value).trim();
};

const getStatusLabel = (percent) => {
  if (percent >= KPI_TARGET) return 'Capai KPI';
  if (percent >= 75) return 'Perlu perhatian';
  return 'Kritikal';
};

const normalizeTime = (value) => {
  if (!value) return '';

  const raw = String(value).trim().replace('.', ':');

  if (/^\d{1,2}:\d{2}$/.test(raw)) {
    const [h, m] = raw.split(':');
    return `${h.padStart(2, '0')}:${m}`;
  }

  return raw;
};

const formatDisplayTime = (timeStr) => {
  if (!timeStr) return '';
  const [hh, mm] = timeStr.split(':');
  const h = Number(hh);
  if (Number.isNaN(h)) return timeStr;
  const ampm = h >= 12 ? 'PM' : 'AM';
  const displayH = h % 12 === 0 ? 12 : h % 12;
  return `${displayH}:${mm} ${ampm}`;
};

const getClassAttendanceKey = (date, schoolName, subject) => [
  String(date || '').trim(),
  normalizeSchoolName(schoolName),
  normalizeSubjectName(subject),
].join('|');

const getIsoDateDiffDays = (laterIso, earlierIso) => {
  if (!laterIso || !earlierIso) return 0;
  const later = new Date(`${laterIso}T00:00:00`);
  const earlier = new Date(`${earlierIso}T00:00:00`);
  return Math.max(0, Math.floor((later.getTime() - earlier.getTime()) / 86400000));
};

const getAttendanceReminderPriority = (daysLate) => {
  if (daysLate <= 0) {
    return { key: 'today', label: 'Hari ini', tone: 'today' };
  }

  if (daysLate <= 2) {
    return { key: 'soon', label: 'Lewat 1-2 hari', tone: 'soon' };
  }

  return { key: 'late', label: 'Lewat 3-14 hari', tone: 'late' };
};

const getAttendanceFollowupId = (row) => {
  const key = [
    row?.date,
    row?.startTime,
    row?.endTime,
    row?.schoolName,
    row?.subject,
    row?.teacher,
  ].map((value) => normalizeCompareValue(value)).join('-');

  return key.replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 140) || 'followup';
};

const toLocalISODate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getScheduleTypeClass = (type) => {
  const value = String(type || '').toLowerCase();
  if (value.includes('play')) return 'play';
  if (value.includes('workshop')) return 'workshop';
  if (value.includes('quiz')) return 'quiz';
  if (value.includes('seminar')) return 'seminar';
  return 'ivc';
};

const flattenSchedules = (sourceSchedules) => {
  if (!Array.isArray(sourceSchedules)) return [];

  return sourceSchedules.flatMap((dayBlock, blockIndex) => {
    const {
      date,
      dayOfWeek,
      daerah,
      terminal,
      schoolName,
      sessions = [],
    } = dayBlock || {};

    return sessions.map((session, sessionIndex) => ({
      id: `${date || 'no-date'}-${schoolName || 'no-school'}-${blockIndex}-${sessionIndex}`,
      date: String(date || '').trim(),
      dayOfWeek: dayOfWeek || '',
      daerah: String(daerah || 'Unknown').trim(),
      terminal: String(terminal || '-').trim(),
      schoolName: normalizeSchoolName(schoolName || 'Unknown'),
      startTime: normalizeTime(session?.startTime),
      endTime: normalizeTime(session?.endTime),
      subject: session?.subject || '-',
      teacher: session?.teacher || '-',
      type: session?.type || '-',
    }));
  });
};

const mapStudentMasterRow = (row, index) => {
  const name = getFirstRowValue(row, [
    'fullname',
    'full name',
    'fullName',
    'Nama',
    'Nama Penuh',
    'Nama Penuh (Huruf Besar)',
  ]);
  const schoolName = normalizeSchoolName(getFirstRowValue(row, [
    'Sekolah',
    'School',
    'Nama Sekolah',
  ]));

  return {
    id: `${normalizeNameForMatch(name)}-${index}`,
    name,
    normalizedName: normalizeNameForMatch(name),
    schoolName,
    terminal: normalizeTerminalName(getFirstRowValue(row, ['Terminal'])),
    daerah: getFirstRowValue(row, ['Daerah', 'District']),
  };
};

const parseAbsentNamesFromSchoolColumn = (value) => String(value || '')
  .split(/[,;\n]+/)
  .map((part) => part.trim())
  .filter(Boolean)
  .filter((part) => !/semua\s+hadir/i.test(part))
  .filter((part) => !/^option\s+\d+$/i.test(part))
  .map((part) => part.replace(/^(\d{1,2})\s*[).:-]?\s+/, '').trim())
  .filter((part) => part.length >= 3);

const SCHOOL_TOKEN_PATTERN = /\b(SMK|SMKC\d*|SMKP|SMK[A-Z0-9]*|SMU|PALOH|CHIKU|KEROH|JELI|BALAH|BATU|MELINTANG|KANDIS|IPOH|TERMINAL|TH|DGM|DKK)\b/g;
const ABSENT_NOTE_PREFIX_PATTERN = /^\s*(?:\d+\s*\/\s*\d+\s*)?(?:tidak\s+hadir|th)?\s*/i;

const getLooseNameWords = (value) => normalizeNameForMatch(value)
  .replace(SCHOOL_TOKEN_PATTERN, ' ')
  .split(' ')
  .filter((word) => word.length >= 3 && !NAME_STOPWORDS.has(word));

const getStudentNameMatchScore = (rawName, student) => {
  const rawNormalized = normalizeNameForMatch(rawName).replace(SCHOOL_TOKEN_PATTERN, ' ').replace(/\s+/g, ' ').trim();
  if (!rawNormalized || !student?.normalizedName) return 0;
  if (rawNormalized === student.normalizedName) return 1000;
  if (student.normalizedName.includes(rawNormalized)) return 800;

  const rawWords = getLooseNameWords(rawName);
  if (!rawWords.length) return 0;

  const studentWords = new Set(student.normalizedName.split(' ').filter(Boolean));
  const matchedWords = rawWords.filter((word) => studentWords.has(word));
  const distinctiveMatches = matchedWords.filter((word) => word.length >= 5).length;

  return (matchedWords.length * 100) + (distinctiveMatches * 40);
};

const getDisplayAbsentStudent = (rawName, schoolName, studentMasterRows, selectedSchoolMeta, options = {}) => {
  const normalizedSchoolName = normalizeSchoolName(schoolName);
  const rawNormalized = normalizeNameForMatch(rawName);
  const candidates = studentMasterRows.filter((student) => {
    const sameSchool = !options.ignoreSchool && normalizedSchoolName && normalizedSchoolName !== 'Unknown'
      ? normalizeSchoolName(student.schoolName) === normalizedSchoolName
      : true;
    const sameTerminal = selectedSchoolMeta?.terminal
      ? normalizeTerminalName(student.terminal) === selectedSchoolMeta.terminal
      : true;
    const sameDaerah = selectedSchoolMeta?.daerah
      ? normalizeCompareValue(student.daerah) === normalizeCompareValue(selectedSchoolMeta.daerah)
      : true;

    return sameSchool && sameTerminal && sameDaerah;
  });

  const best = candidates
    .map((student) => ({
      student,
      score: getStudentNameMatchScore(rawName, student),
    }))
    .filter((item) => item.score >= 200 || (item.score >= 140 && rawNormalized.length >= 5))
    .sort((a, b) => b.score - a.score)[0]?.student;

  if (!best) {
    return {
      id: `${normalizedSchoolName}-${rawNormalized}`,
      name: rawName,
      normalizedName: rawNormalized,
      schoolName: normalizedSchoolName,
    };
  }

  return {
    ...best,
    schoolName: normalizeSchoolName(best.schoolName || normalizedSchoolName),
  };
};

const parseNumberedAbsentNames = (value) => {
  const normalized = String(value || '')
    .replace(/\s+/g, ' ')
    .replace(/\bTH\s+(?=SMK|SMU|Terminal)/gi, ' ')
    .trim();
  if (!normalized) return [];

  const markers = [...normalized.matchAll(/(?:^|[\s:])\d{1,2}\s*[.)]\s*/g)];

  return markers
    .map((marker, index) => {
      const start = marker.index + marker[0].length;
      const end = markers[index + 1]?.index ?? normalized.length;
      return normalized.slice(start, end);
    })
    .map((name) => String(name || '').replace(ABSENT_NOTE_PREFIX_PATTERN, '').trim())
    .map((name) => name.replace(/\s+(?:SMK|SMU|Terminal)\b[^:]*:\s*$/i, '').trim())
    .map((name) => name.replace(/\s*-\s*$/, '').trim())
    .filter((name) => name.length >= 3)
    .filter((name) => !/semua\s+hadir/i.test(name));
};

const getAbsentSchoolHint = (rawName, fallbackSchoolName) => {
  const normalized = normalizeNameForMatch(rawName);
  const knownSchools = Object.keys(DAERAH_BY_SCHOOL).sort((a, b) => b.length - a.length);
  const exactSchool = knownSchools.find((school) => normalized.includes(normalizeNameForMatch(school)));
  if (exactSchool) return normalizeSchoolName(exactSchool);

  if (/\bSMKC\s*2\b|\bSMKC2\b|\bCHIKU\s*2\b/.test(normalized)) return 'SMK Chiku 2';
  if (/\bSMKP\b|\bPALOH\b/.test(normalized)) return 'SMK Paloh';
  if (/\bSMKBC\b|\bSMK\s*BANDAR\s*CHIKU\b|\bBATUK\b/.test(normalized)) return 'SMK Bandar Chiku';

  return normalizeSchoolName(fallbackSchoolName || 'Unknown');
};

const addAbsentStudentToGroup = (groups, rawName, fallbackSchoolName, studentMasterRows, selectedSchoolMeta, options = {}) => {
  const schoolHint = getAbsentSchoolHint(rawName, fallbackSchoolName);
  const student = getDisplayAbsentStudent(rawName, schoolHint, studentMasterRows, selectedSchoolMeta, options);
  const groupKey = normalizeSchoolName(student.schoolName || schoolHint);
  const normalizedName = student.normalizedName || normalizeNameForMatch(student.name);

  if (!groups[groupKey]) {
    groups[groupKey] = {
      schoolName: groupKey,
      students: [],
    };
  }

  if (!groups[groupKey].students.some((item) => item.normalizedName === normalizedName)) {
    groups[groupKey].students.push({
      ...student,
      id: student.id || `${groupKey}-${normalizedName}`,
      normalizedName,
      schoolName: groupKey,
    });
  }
};

const getAbsentStudentsBySchool = (record, studentMasterRows, selectedSchoolMeta) => {
  const checkedColumnGroups = (record?.schoolAbsenceColumns || []).reduce((acc, column) => {
    const names = parseAbsentNamesFromSchoolColumn(column.value);
    if (!names.length) return acc;

    const key = normalizeSchoolName(column.schoolName);
    names.forEach((name) => addAbsentStudentToGroup(acc, name, key, studentMasterRows, selectedSchoolMeta));

    return acc;
  }, {});

  const columnGroups = Object.values(checkedColumnGroups)
    .map((group) => ({
      ...group,
      students: group.students,
    }))
    .sort((a, b) => a.schoolName.localeCompare(b.schoolName));

  if (columnGroups.length) return columnGroups;

  const parsedNoteNames = parseNumberedAbsentNames(record?.absentReason || record?.absenceText || '');
  if (parsedNoteNames.length) {
    const noteGroups = {};

    parsedNoteNames.forEach((name) => {
      addAbsentStudentToGroup(noteGroups, name, record?.schoolName, studentMasterRows, selectedSchoolMeta, {
        ignoreSchool: true,
      });
    });

    return Object.values(noteGroups)
      .map((group) => ({
        ...group,
        students: group.students.sort((a, b) => a.name.localeCompare(b.name)),
      }))
      .sort((a, b) => a.schoolName.localeCompare(b.schoolName));
  }

  const text = normalizeNameForMatch(record?.absenceText || '');
  if (!text) return [];

  const candidates = studentMasterRows.filter((student) => {
    const sameTerminal = selectedSchoolMeta?.terminal
      ? normalizeTerminalName(student.terminal) === selectedSchoolMeta.terminal
      : true;
    const sameDaerah = selectedSchoolMeta?.daerah
      ? normalizeCompareValue(student.daerah) === normalizeCompareValue(selectedSchoolMeta.daerah)
      : true;

    return sameTerminal && sameDaerah && isStudentMentionedInText(text, student);
  });

  const groups = candidates.reduce((acc, student) => {
    const key = normalizeSchoolName(student.schoolName);
    if (!acc[key]) {
      acc[key] = {
        schoolName: key,
        students: [],
      };
    }

    if (!acc[key].students.some((item) => item.normalizedName === student.normalizedName)) {
      acc[key].students.push(student);
    }

    return acc;
  }, {});

  return Object.values(groups)
    .map((group) => ({
      ...group,
      students: group.students.sort((a, b) => a.name.localeCompare(b.name)),
    }))
    .sort((a, b) => a.schoolName.localeCompare(b.schoolName));
};

const getAbsentNotePreview = (value) => {
  const raw = String(value || '')
    .replace(/\s+/g, ' ')
    .trim();

  if (!raw) return '';
  return raw.length > 180 ? `${raw.slice(0, 180)}...` : raw;
};

const AdminDashboard = () => {
  const navigate = useNavigate();

  const [adminData, setAdminData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingSheet, setLoadingSheet] = useState(true);
  const [toast, setToast] = useState({ show: false, type: 'ok', msg: '' });

  const [sheetRows, setSheetRows] = useState([]);
  const [studentMasterRows, setStudentMasterRows] = useState([]);
  const [scheduleViewMode, setScheduleViewMode] = useState('all');
  const [scheduleTimelineMode, setScheduleTimelineMode] = useState('all');
  const [scheduleDisplayMode, setScheduleDisplayMode] = useState('calendar');
  const [scheduleSearch, setScheduleSearch] = useState('');
  const [scheduleDistrictFilter, setScheduleDistrictFilter] = useState('');
  const [scheduleTerminalFilter, setScheduleTerminalFilter] = useState('');
  const [scheduleSubjectFilter, setScheduleSubjectFilter] = useState('');
  const [reflectionDateFilter, setReflectionDateFilter] = useState('');
  const [reflectionSubjectFilter, setReflectionSubjectFilter] = useState('');
  const [reflectionDistrictFilter, setReflectionDistrictFilter] = useState('');
  const [reflectionTerminalFilter, setReflectionTerminalFilter] = useState('');
  const [reflectionSchoolFilter, setReflectionSchoolFilter] = useState('');
  const [reminderDateFilter, setReminderDateFilter] = useState('');
  const [reminderSubjectFilter, setReminderSubjectFilter] = useState('');
  const [reminderDistrictFilter, setReminderDistrictFilter] = useState('');
  const [reminderTerminalFilter, setReminderTerminalFilter] = useState('');
  const [reminderPriorityFilter, setReminderPriorityFilter] = useState('');
  const [reminderSearch, setReminderSearch] = useState('');
  const [globalAdminSearch, setGlobalAdminSearch] = useState('');
  const [activeAdminView, setActiveAdminView] = useState('overview');
  const [firestoreSchedules, setFirestoreSchedules] = useState([]);
  const [loadingSchedulesAdmin, setLoadingSchedulesAdmin] = useState(true);
  const [scheduleForm, setScheduleForm] = useState(EMPTY_SCHEDULE_FORM);
  const [editingScheduleId, setEditingScheduleId] = useState('');
  const [scheduleEditorOpen, setScheduleEditorOpen] = useState(false);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [studentsList, setStudentsList] = useState([]);
  const [teacherProfiles, setTeacherProfiles] = useState([]);
  const [performanceForm, setPerformanceForm] = useState(EMPTY_PERFORMANCE_FORM);
  const [savingPerformance, setSavingPerformance] = useState(false);
  const [importingPerformance, setImportingPerformance] = useState(false);
  const [performanceRecords, setPerformanceRecords] = useState([]);
  const [loadingPerformanceRecords, setLoadingPerformanceRecords] = useState(true);
  const [teacherFeedback, setTeacherFeedback] = useState([]);
  const [attendanceFollowups, setAttendanceFollowups] = useState({});
  const [showAllAttendanceMatrix, setShowAllAttendanceMatrix] = useState(false);

  const todayISO = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [scheduleCalendarMonth, setScheduleCalendarMonth] = useState(() => new Date(`${new Date().toISOString().slice(0, 7)}-01T00:00:00`));
  const [scheduleCalendarDate, setScheduleCalendarDate] = useState(todayISO);
  const [hasAnchoredScheduleCalendar, setHasAnchoredScheduleCalendar] = useState(false);
  const [selectedDate] = useState(todayISO);
  const [selectedDistrict, setSelectedDistrict] = useState('');
  const [selectedTerminal, setSelectedTerminal] = useState('');
  const [selectedSchool, setSelectedSchool] = useState('');
  const [selectedAttendanceSession, setSelectedAttendanceSession] = useState('');
  const [showMobileLocationFilters, setShowMobileLocationFilters] = useState(false);

  const showToast = (type, msg) => {
    setToast({ show: true, type, msg });
    setTimeout(() => setToast({ show: false, type: 'ok', msg: '' }), 2600);
  };

  const downloadCsv = (filename, columns, rows) => {
    if (!rows.length) {
      showToast('bad', 'Tiada data untuk dimuat turun.');
      return;
    }

    const csv = [
      columns.map((column) => escapeCsvValue(column.label)).join(','),
      ...rows.map((row) => columns.map((column) => escapeCsvValue(column.value(row))).join(',')),
    ].join('\n');

    const blob = new Blob([`\ufeff${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    showToast('ok', 'CSV berjaya dimuat turun.');
  };

  const formatMalayDate = (isoDate) => {
    if (!isoDate) return '';
    const d = new Date(`${isoDate}T00:00:00`);
    const days = ['Ahad', 'Isnin', 'Selasa', 'Rabu', 'Khamis', 'Jumaat', 'Sabtu'];
    const months = ['Jan', 'Feb', 'Mac', 'Apr', 'Mei', 'Jun', 'Jul', 'Ogos', 'Sep', 'Okt', 'Nov', 'Dis'];
    return `${days[d.getDay()]}, ${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
  };

  const getDayOfWeek = (isoDate) => {
    if (!isoDate) return '';
    const d = new Date(`${isoDate}T00:00:00`);
    return ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][d.getDay()];
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Selamat pagi';
    if (hour < 17) return 'Selamat tengahari';
    return 'Selamat petang';
  };

  const normalizeSubjectKey = (subject) => {
    const key = String(subject || '').trim().toUpperCase();
    return PERFORMANCE_SUBJECT_ALIASES[key] || key;
  };

  const normalizeGrade = (grade) => {
    const key = String(grade || '').trim().toUpperCase();
    return PERFORMANCE_GRADES.includes(key) ? key : '-';
  };

  const toNullableNumber = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
  };

  const toOptionalMark = (value) => {
    if (value === null || value === undefined || String(value).trim() === '') return null;
    const number = Number(value);
    return Number.isFinite(number) ? Math.max(0, Math.min(100, number)) : null;
  };

  useEffect(() => {
    const fetchAdmin = async () => {
      try {
        const user = auth.currentUser;
        if (!user) {
          navigate('/login');
          return;
        }

        const snap = await getDoc(doc(db, 'users', user.uid));
        if (!snap.exists()) {
          navigate('/login');
          return;
        }

        const data = { uid: user.uid, ...snap.data() };
        const userEmail = user.email?.toLowerCase() || '';

        if (data.role !== 'admin' || !ADMIN_ALLOWED_EMAILS.includes(userEmail)) {
          await signOut(auth);
          navigate('/login');
          return;
        }

        setAdminData(data);
      } catch (error) {
        console.error('fetchAdmin error:', error);
        showToast('bad', 'Ralat memuatkan akaun pentadbir.');
      } finally {
        setLoading(false);
      }
    };

    fetchAdmin();
  }, [navigate]);

  useEffect(() => {
    const fetchSheetData = async () => {
      try {
        setLoadingSheet(true);

        const res = await fetch(SHEET_CSV_URL);
        const text = await res.text();

        const parsed = Papa.parse(text, {
          header: true,
          skipEmptyLines: true,
        });

        if (parsed.errors?.length) {
          console.warn('PapaParse warnings:', parsed.errors);
        }

        setSheetRows(parsed.data || []);
      } catch (error) {
        console.error('fetchSheetData error:', error);
        showToast('bad', 'Gagal baca Google Sheet.');
      } finally {
        setLoadingSheet(false);
      }
    };

    fetchSheetData();
  }, []);

  useEffect(() => {
    const fetchStudentMaster = async () => {
      try {
        const res = await fetch(STUDENT_MASTER_CSV_URL);
        const text = await res.text();

        const parsed = Papa.parse(text, {
          header: true,
          skipEmptyLines: true,
        });

        if (parsed.errors?.length) {
          console.warn('Student master PapaParse warnings:', parsed.errors);
        }

        setStudentMasterRows((parsed.data || []).map(mapStudentMasterRow).filter((student) => student.name && student.schoolName));
      } catch (error) {
        console.error('fetchStudentMaster error:', error);
        showToast('bad', 'Gagal baca master list pelajar.');
      }
    };

    fetchStudentMaster();
  }, []);

  useEffect(() => {
    const fetchTeacherFeedback = async () => {
      try {
        const snap = await getDocs(collection(db, 'teacher_class_feedback'));
        const list = snap.docs.map((item) => ({
          id: item.id,
          ...item.data()
        }));

        list.sort((a, b) => {
          const dateCompare = (b.scheduleDateISO || '').localeCompare(a.scheduleDateISO || '');
          if (dateCompare !== 0) return dateCompare;
          return (b.scheduleStartTime || '').localeCompare(a.scheduleStartTime || '');
        });

        setTeacherFeedback(list);
      } catch (error) {
        console.error('fetchTeacherFeedback error:', error);
        showToast('bad', 'Gagal baca maklum balas cikgu.');
      }
    };

    if (!loading && adminData?.role === 'admin') {
      fetchTeacherFeedback();
    }
  }, [adminData, loading]);

  useEffect(() => {
    const fetchFirestoreSchedules = async () => {
      try {
        setLoadingSchedulesAdmin(true);
        const snap = await getDocs(collection(db, 'schedules'));
        const list = snap.docs.map((item) => ({
          id: item.id,
          source: 'firestore',
          ...item.data()
        })).map((schedule) => ({
          ...schedule,
          schoolName: getScheduleSchoolName(schedule)
        }));

        list.sort((a, b) => {
          const dateCompare = (a.date || '').localeCompare(b.date || '');
          if (dateCompare !== 0) return dateCompare;
          return (a.startTime || '').localeCompare(b.startTime || '');
        });

        setFirestoreSchedules(list);
      } catch (error) {
        console.error('fetchFirestoreSchedules error:', error);
        showToast('bad', 'Gagal baca jadual Firestore.');
      } finally {
        setLoadingSchedulesAdmin(false);
      }
    };

    if (!loading && adminData?.role === 'admin') {
      fetchFirestoreSchedules();
    }
  }, [adminData, loading]);

  useEffect(() => {
    const fetchStudents = async () => {
      try {
        const snap = await getDocs(collection(db, 'users'));
        const users = snap.docs.map((item) => ({ uid: item.id, ...item.data() }));
        const list = users
          .filter((item) => item.role === 'student')
          .sort((a, b) => (a.fullName || '').localeCompare(b.fullName || ''));

        setStudentsList(list);
        setTeacherProfiles(users.filter((item) => item.role === 'teacher'));
      } catch (error) {
        console.error('fetchStudents error:', error);
        showToast('bad', 'Gagal baca senarai pelajar.');
      }
    };

    if (!loading && adminData?.role === 'admin') {
      fetchStudents();
    }
  }, [adminData, loading]);

  useEffect(() => {
    const fetchPerformanceRecords = async () => {
      try {
        setLoadingPerformanceRecords(true);
        const snap = await getDocs(collection(db, 'student_performance'));
        const list = snap.docs.map((item) => ({
          id: item.id,
          ...item.data()
        }));

        setPerformanceRecords(list);
      } catch (error) {
        console.error('fetchPerformanceRecords error:', error);
        showToast('bad', 'Gagal membaca graf prestasi.');
      } finally {
        setLoadingPerformanceRecords(false);
      }
    };

    if (!loading && adminData?.role === 'admin') {
      fetchPerformanceRecords();
    }
  }, [adminData, loading]);

  useEffect(() => {
    const fetchAttendanceFollowups = async () => {
      try {
        const snap = await getDocs(collection(db, 'attendance_followups'));
        const mapped = snap.docs.reduce((acc, item) => {
          acc[item.id] = { id: item.id, ...item.data() };
          return acc;
        }, {});

        setAttendanceFollowups(mapped);
      } catch (error) {
        console.error('fetchAttendanceFollowups error:', error);
        showToast('bad', 'Gagal baca status peringatan kehadiran.');
      }
    };

    if (!loading && adminData?.role === 'admin') {
      fetchAttendanceFollowups();
    }
  }, [adminData, loading]);

  useEffect(() => {
    if (performanceForm.studentId || studentsList.length === 0) return;

    const studentWithRecord = performanceRecords.find((record) => (
      studentsList.some((student) => student.uid === record.studentId)
    ));

    setPerformanceForm((current) => ({
      ...current,
      studentId: studentWithRecord?.studentId || studentsList[0].uid,
    }));
  }, [performanceForm.studentId, performanceRecords, studentsList]);

  const masterSchedules = useMemo(() => {
    const rawSchedules = Array.isArray(schedulesData)
      ? schedulesData
      : schedulesData?.default && Array.isArray(schedulesData.default)
      ? schedulesData.default
      : schedulesData?.schedules && Array.isArray(schedulesData.schedules)
      ? schedulesData.schedules
      : [];

    return flattenSchedules(rawSchedules);
  }, []);

  const adminSchedules = useMemo(() => {
    const getMergeKey = (schedule) => [
      normalizeCompareValue(schedule.date),
      normalizeCompareValue(getScheduleSchoolName(schedule)),
      normalizeCompareValue(schedule.subject),
      normalizeTime(schedule.startTime),
    ].join('|');

    const scheduleMap = new Map();

    masterSchedules.forEach((schedule) => {
      const normalizedSchedule = {
        ...schedule,
        schoolName: getScheduleSchoolName(schedule),
        source: 'static',
      };
      scheduleMap.set(getMergeKey(normalizedSchedule), normalizedSchedule);
    });

    firestoreSchedules.forEach((schedule) => {
      const matchingStaticSchedule = masterSchedules.find((candidate) => {
        const sameDate = normalizeCompareValue(candidate.date) === normalizeCompareValue(schedule.date);
        const sameTime = normalizeTime(candidate.startTime) === normalizeTime(schedule.startTime);
        const sameSubject = normalizeCompareValue(candidate.subject) === normalizeCompareValue(schedule.subject);
        const sameSchool = normalizeCompareValue(getScheduleSchoolName(candidate)) === normalizeCompareValue(getScheduleSchoolName(schedule));
        return sameDate && sameTime && sameSubject && sameSchool;
      });

      const normalizedSchedule = {
        ...(matchingStaticSchedule || {}),
        ...schedule,
        schoolName: isMissingSchoolName(schedule.schoolName)
          ? getScheduleSchoolName(matchingStaticSchedule || schedule)
          : getScheduleSchoolName(schedule),
        daerah: isMissingSchoolName(schedule.daerah)
          ? matchingStaticSchedule?.daerah || schedule.daerah
          : schedule.daerah,
        terminal: !schedule.terminal || schedule.terminal === '-'
          ? matchingStaticSchedule?.terminal || schedule.terminal
          : schedule.terminal,
        source: 'firestore',
      };

      scheduleMap.set(getMergeKey(normalizedSchedule), normalizedSchedule);
    });

    return [...scheduleMap.values()].sort((a, b) => {
      const dateCompare = (a.date || '').localeCompare(b.date || '');
      if (dateCompare !== 0) return dateCompare;
      const schoolCompare = (a.schoolName || '').localeCompare(b.schoolName || '');
      if (schoolCompare !== 0) return schoolCompare;
      return (a.startTime || '').localeCompare(b.startTime || '');
    });
  }, [firestoreSchedules, masterSchedules]);

  const scheduleFilterOptions = useMemo(() => {
    const districts = new Set();
    const terminals = new Set();
    const subjects = new Set();

    adminSchedules.forEach((schedule) => {
      if (schedule.daerah) districts.add(String(schedule.daerah).trim());
      if (schedule.terminal) terminals.add(normalizeTerminalName(schedule.terminal));
      if (schedule.subject) subjects.add(String(schedule.subject).trim());
    });

    return {
      districts: [...districts].sort(),
      terminals: [...terminals].sort((a, b) => {
        const aNumber = Number(String(a).match(/\d+/)?.[0] || 999);
        const bNumber = Number(String(b).match(/\d+/)?.[0] || 999);
        return aNumber - bNumber || a.localeCompare(b);
      }),
      subjects: [...subjects].sort(),
    };
  }, [adminSchedules]);

  const resetScheduleFilters = () => {
    setScheduleSearch('');
    setScheduleDistrictFilter('');
    setScheduleTerminalFilter('');
    setScheduleSubjectFilter('');
  };

  const cleanedAttendanceRecords = useMemo(() => {
    return sheetRows
      .map((row, index) => {
        const schoolName = detectSchoolFromRow(row);
        const daerah = inferDaerah(row, schoolName);
        const terminal = inferTerminal(row, schoolName);
        const teacher = cleanTeacherName(row['Guru mata pelajaran']);
        const subject = normalizeSubjectName(row['Guru mata pelajaran']);
        const session = normalizeSessionName(row['Sesi']);
        const date = parseSheetDate(row['Tarikh Sesi'] || row['Timestamp'], row['Timestamp']);
        const attendanceText = row['Jumlah Kehadiran Pelajar (cnth: 23/25)'];
        const absentReason = getRowField(row, 'Jika pelajar tidak hadir');
        const schoolAbsenceColumns = getSchoolColumnEntries(row);
        const attendance = parseAttendanceFraction(attendanceText);

        return {
          id: `${date || 'no-date'}-${schoolName}-${index}`,
          date,
          timestamp: row['Timestamp'] || '',
          timestampMs: getSheetTimestampMs(row['Timestamp']),
          teacher,
          subject,
          session,
          terminal,
          daerah,
          schoolName,
          attendanceText: attendanceText || '',
          absentReason: absentReason || '',
          schoolAbsenceColumns,
          absenceText: [attendanceText, absentReason].filter(Boolean).join(' '),
          present: attendance.present,
          total: attendance.total,
          percent: attendance.percent,
          mastery: getRowField(row, 'Tahap penguasaan pelajar terhadap topik hari ini'),
          engagement: getRowField(row, 'Tahap penglibatan pelajar dalam kelas hari ini'),
          objective: getRowField(row, 'Adakah objektif pembelajaran hari ini tercapai?'),
          challenge: getRowField(row, 'Cabaran utama semasa kelas dijalankan'),
          observation: getRowField(row, 'Ceritakan secara ringkas perubahan yang diperhatikan dalam diri pelajar'),
          status: getStatusLabel(attendance.percent),
        };
      })
      .filter((item) => item.date && item.schoolName !== 'Unknown');
  }, [sheetRows]);

  const schoolOptions = useMemo(() => {
    const fromTerminalColumn = cleanedAttendanceRecords
      .map((record) => normalizeSchoolName(record.schoolName))
      .filter((school) => !isMissingSchoolName(school));
    const fromSchedules = adminSchedules.map((s) => normalizeSchoolName(s.schoolName)).filter((school) => !isMissingSchoolName(school));
    const fromFeedback = teacherFeedback.map((r) => normalizeSchoolName(r.schoolName)).filter((school) => !isMissingSchoolName(school));
    return [...new Set([...fromTerminalColumn, ...fromSchedules, ...fromFeedback])].sort();
  }, [adminSchedules, cleanedAttendanceRecords, teacherFeedback]);

  const districtOptions = useMemo(() => {
    return [...new Set(MANUAL_SCHOOL_OPTIONS.map((school) => school.daerah).filter(Boolean))].sort();
  }, []);

  const terminalOptions = useMemo(() => {
    return [...new Set(
      MANUAL_SCHOOL_OPTIONS
        .filter((school) => (selectedDistrict ? school.daerah === selectedDistrict : true))
        .map((school) => normalizeTerminalName(school.terminal))
        .filter(Boolean)
    )].sort((a, b) => {
      const aNumber = Number(String(a).match(/\d+/)?.[0] || 999);
      const bNumber = Number(String(b).match(/\d+/)?.[0] || 999);
      return aNumber - bNumber || a.localeCompare(b);
    });
  }, [selectedDistrict]);

  const overviewSchoolOptions = useMemo(() => {
    return schoolOptions.filter((school) => {
      const meta = getManualSchoolMeta(school);
      const matchDistrict = selectedDistrict ? meta.daerah === selectedDistrict : true;
      const matchTerminal = selectedTerminal ? meta.terminal === selectedTerminal : true;
      return matchDistrict && matchTerminal;
    });
  }, [schoolOptions, selectedDistrict, selectedTerminal]);

  const manualSchoolNames = useMemo(() => {
    return MANUAL_SCHOOL_OPTIONS.map((school) => school.name).sort();
  }, []);

  const selectedPerformanceStudent = useMemo(() => {
    return studentsList.find((student) => student.uid === performanceForm.studentId) || null;
  }, [performanceForm.studentId, studentsList]);

  const adminPerformanceBySubject = useMemo(() => {
    const studentRecords = performanceRecords.filter((record) => (
      performanceForm.studentId ? record.studentId === performanceForm.studentId : false
    ));
    const recordMap = new Map(
      studentRecords.map((record) => [normalizeSubjectKey(record.subject), record])
    );

    return PERFORMANCE_SUBJECTS.map((subject) => {
      const record = recordMap.get(subject) || {};
      const hasRecord = Boolean(record.id || record.studentId || record.baselineGrade || record.currentGrade);
      const baselineGrade = normalizeGrade(record.baselineGrade);
      const currentGrade = normalizeGrade(record.currentGrade);
      const baselineIndex = PERFORMANCE_GRADES.indexOf(baselineGrade);
      const currentIndex = PERFORMANCE_GRADES.indexOf(currentGrade);
      const targetIndex = baselineIndex >= 0 ? Math.max(0, baselineIndex - 2) : -1;
      const targetGrade = targetIndex >= 0 ? PERFORMANCE_GRADES[targetIndex] : '-';
      const achieved = currentIndex >= 0 && targetIndex >= 0 && currentIndex <= targetIndex;
      const baselineMark = toNullableNumber(record.baselineMark);
      const currentMark = toNullableNumber(record.currentMark);
      const emptyMarkPair = baselineMark === 0 && currentMark === 0 && !record.baselineMarkEntered && !record.currentMarkEntered;
      const displayBaselineMark = emptyMarkPair ? null : baselineMark;
      const displayCurrentMark = emptyMarkPair ? null : currentMark;
      const markProgress = displayCurrentMark === null ? 0 : Math.max(0, Math.min(100, displayCurrentMark));

      return {
        subject,
        ...record,
        hasRecord,
        baselineGrade,
        currentGrade,
        targetGrade,
        achieved,
        baselineMark: displayBaselineMark,
        currentMark: displayCurrentMark,
        markProgress,
        markDelta: displayBaselineMark === null || displayCurrentMark === null ? null : displayCurrentMark - displayBaselineMark,
      };
    });
  }, [performanceForm.studentId, performanceRecords]);

  const adminPerformanceSummary = useMemo(() => {
    const withRecord = adminPerformanceBySubject.filter((item) => item.hasRecord);
    const achieved = adminPerformanceBySubject.filter((item) => item.achieved).length;
    const currentMarks = withRecord
      .map((item) => item.currentMark)
      .filter((mark) => Number.isFinite(mark));
    const averageMark = currentMarks.length
      ? Math.round(currentMarks.reduce((sum, mark) => sum + mark, 0) / currentMarks.length)
      : null;

    return {
      withRecord: withRecord.length,
      achieved,
      averageMark,
      progressPercent: Math.round((achieved / PERFORMANCE_SUBJECTS.length) * 100),
    };
  }, [adminPerformanceBySubject]);

  const filteredSchedules = useMemo(() => {
    return adminSchedules
      .filter((schedule) => {
        const scheduleDate = String(schedule.date || '').trim();
        const filterDate = String(selectedDate || '').trim();

        const scheduleSchool = String(schedule.schoolName || '').trim();
        const filterSchool = String(selectedSchool || '').trim();

        const matchDate = filterDate ? scheduleDate === filterDate : true;
        const matchSchool = filterSchool ? scheduleSchool === filterSchool : true;

        return matchDate && matchSchool;
      })
      .sort((a, b) => {
        const timeCompare = (a.startTime || '').localeCompare(b.startTime || '');
        if (timeCompare !== 0) return timeCompare;
        return (a.subject || '').localeCompare(b.subject || '');
      });
  }, [adminSchedules, selectedDate, selectedSchool]);

  const filteredAttendanceRecords = useMemo(() => {
    return cleanedAttendanceRecords.filter((record) => {
      const matchSchool = selectedSchool ? record.schoolName === selectedSchool : true;
      return matchSchool;
    });
  }, [cleanedAttendanceRecords, selectedSchool]);

  const selectedSchoolMeta = useMemo(() => {
    if (!selectedSchool) {
      return {
        schoolName: '',
        daerah: '',
        terminal: '',
      };
    }

    return getManualSchoolMeta(selectedSchool);
  }, [selectedSchool]);

  useEffect(() => {
    if (!selectedSchool) return;

    const stillVisible = overviewSchoolOptions.some((school) => normalizeSchoolName(school) === normalizeSchoolName(selectedSchool));
    if (!stillVisible) {
      setSelectedSchool('');
      setSelectedAttendanceSession('');
    }
  }, [overviewSchoolOptions, selectedSchool]);

  const terminalAttendanceRecords = useMemo(() => {
    if (!selectedSchoolMeta.terminal) return [];

    return cleanedAttendanceRecords.filter((record) => {
      const sameTerminal = normalizeTerminalName(record.terminal) === selectedSchoolMeta.terminal;
      const sameDaerah = selectedSchoolMeta.daerah
        ? normalizeCompareValue(record.daerah) === normalizeCompareValue(selectedSchoolMeta.daerah)
        : true;

      return sameTerminal && sameDaerah;
    });
  }, [cleanedAttendanceRecords, selectedSchoolMeta.daerah, selectedSchoolMeta.terminal]);

  const attendanceScopeRecords = useMemo(() => {
    if (!selectedSchool) return [];

    const exactSchoolRecords = cleanedAttendanceRecords.filter((record) => (
      normalizeSchoolName(record.schoolName) === selectedSchoolMeta.schoolName
    ));

    return exactSchoolRecords.length ? exactSchoolRecords : terminalAttendanceRecords;
  }, [cleanedAttendanceRecords, selectedSchool, selectedSchoolMeta.schoolName, terminalAttendanceRecords]);

  const scheduleLocationScope = useMemo(() => {
    const hasManualLocationFilter = Boolean(scheduleDistrictFilter || scheduleTerminalFilter);
    const district = hasManualLocationFilter
      ? String(scheduleDistrictFilter || '').trim()
      : String(selectedSchoolMeta.daerah || '').trim();
    const terminal = hasManualLocationFilter
      ? normalizeTerminalName(scheduleTerminalFilter)
      : normalizeTerminalName(selectedSchoolMeta.terminal);
    const hasScope = Boolean(district || terminal);

    const labelParts = [
      district || (terminal ? 'Semua daerah' : ''),
      terminal || (district ? 'Semua terminal' : ''),
    ].filter(Boolean);

    return {
      district,
      terminal,
      hasScope,
      label: hasScope ? labelParts.join(' / ') : 'Pilih sekolah atau terminal',
    };
  }, [scheduleDistrictFilter, scheduleTerminalFilter, selectedSchoolMeta.daerah, selectedSchoolMeta.terminal]);

  const attendanceSessionOptions = useMemo(() => {
    if (!selectedSchool) return [];

    return [...new Set(
      attendanceScopeRecords
        .filter((record) => record.subject !== 'LAIN' && record.total)
        .map((record) => record.session)
        .filter(Boolean)
    )].sort((a, b) => {
      const sessionCompare = getSessionSortValue(a) - getSessionSortValue(b);
      if (sessionCompare !== 0) return sessionCompare;
      return a.localeCompare(b);
    });
  }, [attendanceScopeRecords, selectedSchool]);

  const attendanceSessionDateMap = useMemo(() => {
    return attendanceScopeRecords.reduce((acc, record) => {
      if (!record.session || !record.date || !record.total || record.subject === 'LAIN') return acc;
      if (!acc[record.session]) acc[record.session] = new Set();
      acc[record.session].add(record.date);
      return acc;
    }, {});
  }, [attendanceScopeRecords]);

  const getAttendanceSessionLabel = (session) => {
    const dates = [...(attendanceSessionDateMap[session] || [])].sort();
    if (!dates.length) return session;
    if (dates.length === 1) return `${session} - ${formatMalayDate(dates[0])}`;
    return `${session} - ${dates.map((date) => formatMalayDate(date)).join(', ')}`;
  };

  const selectedAttendanceSessionDateLabel = useMemo(() => {
    if (!selectedAttendanceSession) return '';
    const dates = [...(attendanceSessionDateMap[selectedAttendanceSession] || [])].sort();
    if (!dates.length) return '';
    if (dates.length === 1) return formatMalayDate(dates[0]);
    return dates.map((date) => formatMalayDate(date)).join(' / ');
  }, [attendanceSessionDateMap, selectedAttendanceSession]);

  useEffect(() => {
    if (!attendanceSessionOptions.length) {
      setSelectedAttendanceSession('');
      return;
    }

    if (!selectedAttendanceSession || !attendanceSessionOptions.includes(selectedAttendanceSession)) {
      setSelectedAttendanceSession(attendanceSessionOptions[attendanceSessionOptions.length - 1]);
    }
  }, [attendanceSessionOptions, selectedAttendanceSession]);

  const overviewSubjectAttendance = useMemo(() => {
    const groups = PERFORMANCE_SUBJECTS.reduce((acc, subject) => {
      acc[subject] = {
        subject,
        present: 0,
        total: 0,
        percent: null,
        sessions: 0,
        absentBySchool: [],
        absentNote: '',
      };
      return acc;
    }, {});

    if (!selectedSchool || !selectedAttendanceSession) return Object.values(groups);

    const latestBySubject = {};

    attendanceScopeRecords.forEach((record) => {
      if (record.session !== selectedAttendanceSession || record.subject === 'LAIN' || !record.total) return;
      if (!groups[record.subject]) return;

      if (!latestBySubject[record.subject] || record.timestampMs > latestBySubject[record.subject].timestampMs) {
        latestBySubject[record.subject] = record;
      }
    });

    Object.values(latestBySubject).forEach((record) => {
      groups[record.subject].present = record.present;
      groups[record.subject].total = record.total;
      groups[record.subject].sessions = 1;
      groups[record.subject].date = record.date;
      groups[record.subject].absentBySchool = getAbsentStudentsBySchool(record, studentMasterRows, selectedSchoolMeta);
      groups[record.subject].absentNote = getAbsentNotePreview(record.absenceText);
    });

    return Object.values(groups).map((item) => ({
      ...item,
      percent: item.total ? Math.round((item.present / item.total) * 100) : null,
    }));
  }, [attendanceScopeRecords, selectedAttendanceSession, selectedSchool, selectedSchoolMeta, studentMasterRows]);

  const overviewAttendanceHealth = useMemo(() => {
    return overviewSubjectAttendance.reduce(
      (acc, item) => {
        if (item.percent === null) {
          acc.empty += 1;
        } else if (item.percent >= KPI_TARGET) {
          acc.good += 1;
        } else if (item.percent >= 75) {
          acc.warn += 1;
        } else {
          acc.bad += 1;
        }
        return acc;
      },
      { good: 0, warn: 0, bad: 0, empty: 0 }
    );
  }, [overviewSubjectAttendance]);

  const teacherReflectionRecords = useMemo(() => {
    return cleanedAttendanceRecords
      .filter((record) => (
        record.mastery ||
        record.engagement ||
        record.objective ||
        record.challenge ||
        record.observation
      ))
      .sort((a, b) => {
        const timestampCompare = (b.timestampMs || 0) - (a.timestampMs || 0);
        if (timestampCompare !== 0) return timestampCompare;
        return (b.date || '').localeCompare(a.date || '');
      });
  }, [cleanedAttendanceRecords]);

  const reflectionFilterOptions = useMemo(() => {
    const dates = new Set();
    const subjects = new Set();
    const districts = new Set();
    const terminals = new Set();
    const schools = new Set();

    teacherReflectionRecords.forEach((record) => {
      if (record.date) dates.add(record.date);
      if (record.subject) subjects.add(record.subject);
      if (record.daerah) districts.add(record.daerah);
      if (record.terminal) terminals.add(normalizeTerminalName(record.terminal));
      if (record.schoolName) schools.add(normalizeSchoolName(record.schoolName));
    });

    return {
      dates: [...dates].sort((a, b) => b.localeCompare(a)),
      subjects: [...subjects].sort(),
      districts: [...districts].sort(),
      terminals: [...terminals].sort((a, b) => {
        const aNumber = Number(String(a).match(/\d+/)?.[0] || 999);
        const bNumber = Number(String(b).match(/\d+/)?.[0] || 999);
        return aNumber - bNumber || a.localeCompare(b);
      }),
      schools: [...schools].sort(),
    };
  }, [teacherReflectionRecords]);

  const filteredTeacherReflections = useMemo(() => {
    return teacherReflectionRecords.filter((record) => {
      const activeSchoolFilter = reflectionSchoolFilter || selectedSchool;
      const matchDate = reflectionDateFilter ? record.date === reflectionDateFilter : true;
      const matchSubject = reflectionSubjectFilter ? record.subject === reflectionSubjectFilter : true;
      const matchDistrict = reflectionDistrictFilter ? record.daerah === reflectionDistrictFilter : true;
      const matchTerminal = reflectionTerminalFilter ? normalizeTerminalName(record.terminal) === reflectionTerminalFilter : true;
      const matchSchool = activeSchoolFilter
        ? normalizeSchoolName(record.schoolName) === normalizeSchoolName(activeSchoolFilter)
        : true;
      return matchDate && matchSubject && matchDistrict && matchTerminal && matchSchool;
    });
  }, [
    reflectionDateFilter,
    reflectionDistrictFilter,
    reflectionSchoolFilter,
    reflectionSubjectFilter,
    reflectionTerminalFilter,
    selectedSchool,
    teacherReflectionRecords,
  ]);

  const activeReflectionFilterCount = [
    reflectionDateFilter,
    reflectionSubjectFilter,
    reflectionDistrictFilter,
    reflectionTerminalFilter,
    reflectionSchoolFilter,
  ].filter(Boolean).length;

  const resetReflectionFilters = () => {
    setReflectionDateFilter('');
    setReflectionSubjectFilter('');
    setReflectionDistrictFilter('');
    setReflectionTerminalFilter('');
    setReflectionSchoolFilter('');
  };

  const teacherReflectionSummary = useMemo(() => {
    const objectiveAchieved = filteredTeacherReflections.filter((record) => (
      parseLikertScore(record.objective) >= 4 ||
      /ya|yes|tercapai|capai/i.test(String(record.objective || ''))
    )).length;
    const withChallenge = filteredTeacherReflections.filter((record) => String(record.challenge || '').trim()).length;

    return {
      total: filteredTeacherReflections.length,
      objectiveAchieved,
      withChallenge,
      withObservation: filteredTeacherReflections.filter((record) => String(record.observation || '').trim()).length,
    };
  }, [filteredTeacherReflections]);

  const reflectionLikertSummary = useMemo(() => {
    const metrics = [
      { key: 'mastery', label: 'Penguasaan Topik', low: 'Sangat lemah', high: 'Sangat baik' },
      { key: 'engagement', label: 'Penglibatan Kelas', low: 'Pasif', high: 'Sangat aktif' },
      { key: 'objective', label: 'Objektif Pembelajaran', low: 'Tidak', high: 'Ya, sepenuhnya' },
    ];

    return metrics.map((metric) => {
      const counts = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
      filteredTeacherReflections.forEach((record) => {
        const score = parseLikertScore(record[metric.key]);
        if (score) counts[score] += 1;
      });

      const total = Object.values(counts).reduce((sum, count) => sum + count, 0);
      const average = total
        ? Object.entries(counts).reduce((sum, [score, count]) => sum + Number(score) * count, 0) / total
        : 0;

      return {
        ...metric,
        counts,
        total,
        average,
        averageLabel: total ? average.toFixed(1) : '-',
      };
    });
  }, [filteredTeacherReflections]);

  const reportSubmittedSchoolSet = useMemo(() => {
    const set = new Set();

    cleanedAttendanceRecords.forEach((record) => {
      const sameDate = String(record.date || '').trim() === String(selectedDate || '').trim();
      const sameSchool = selectedSchool ? record.schoolName === selectedSchool : true;

      if (sameDate && sameSchool) {
        set.add(String(record.schoolName || '').trim());
      }
    });

    return set;
  }, [cleanedAttendanceRecords, selectedDate, selectedSchool]);

  const reportSubmittedScheduleSet = useMemo(() => {
    const set = new Set();

    cleanedAttendanceRecords.forEach((record) => {
      const date = String(record.date || '').trim();
      const school = String(record.schoolName || '').trim();

      if (date && school) {
        set.add(`${date}|${school}`);
      }
    });

    return set;
  }, [cleanedAttendanceRecords]);

  const attendanceSubmittedClassSet = useMemo(() => {
    const set = new Set();

    cleanedAttendanceRecords.forEach((record) => {
      if (!record.date || !record.schoolName || !record.subject || !record.total) return;
      set.add(getClassAttendanceKey(record.date, record.schoolName, record.subject));
    });

    return set;
  }, [cleanedAttendanceRecords]);

  const groupedSchedules = useMemo(() => {
    const groups = {};

    filteredSchedules.forEach((schedule) => {
      const school = schedule.schoolName || 'Sekolah Tidak Diketahui';
      if (!groups[school]) groups[school] = [];
      groups[school].push(schedule);
    });

    return groups;
  }, [filteredSchedules]);

  const schoolsWithSchedulesToday = useMemo(() => {
    return Object.keys(groupedSchedules);
  }, [groupedSchedules]);

  const pendingReportSchools = useMemo(() => {
    return schoolsWithSchedulesToday.filter(
      (school) => !reportSubmittedSchoolSet.has(String(school || '').trim())
    );
  }, [schoolsWithSchedulesToday, reportSubmittedSchoolSet]);

  const visibleGroupedSchedules = useMemo(() => {
    if (scheduleViewMode === 'pending') {
      return Object.fromEntries(
        Object.entries(groupedSchedules).filter(([schoolName]) =>
          pendingReportSchools.includes(schoolName)
        )
      );
    }

    return groupedSchedules;
  }, [groupedSchedules, pendingReportSchools, scheduleViewMode]);

  const visibleSchoolsCount = Object.keys(visibleGroupedSchedules).length;
  const visibleClassesCount = Object.values(visibleGroupedSchedules).reduce(
    (sum, rows) => sum + rows.length,
    0
  );

  const scheduleTimelineRows = useMemo(() => {
    const queryText = normalizeCompareValue(scheduleSearch);

    const rows = adminSchedules
      .filter((schedule) => {
        const scheduleDate = String(schedule.date || '').trim();
        const scheduleDistrict = String(schedule.daerah || '').trim();
        const scheduleTerminal = normalizeTerminalName(schedule.terminal);
        const scheduleSubject = String(schedule.subject || '').trim();

        const matchTimeline =
          scheduleTimelineMode === 'today'
            ? scheduleDate === todayISO
            : scheduleTimelineMode === 'upcoming'
            ? scheduleDate > todayISO
            : scheduleTimelineMode === 'past'
            ? scheduleDate < todayISO
            : true;

        const matchDistrict = scheduleLocationScope.district
          ? normalizeCompareValue(scheduleDistrict) === normalizeCompareValue(scheduleLocationScope.district)
          : true;
        const matchTerminal = scheduleLocationScope.terminal
          ? scheduleTerminal === scheduleLocationScope.terminal
          : true;
        const matchSubject = scheduleSubjectFilter ? scheduleSubject === scheduleSubjectFilter : true;
        const matchSearch = queryText
          ? normalizeCompareValue([
              schedule.schoolName,
              schedule.daerah,
              schedule.terminal,
              schedule.subject,
              schedule.teacher,
              schedule.type,
              schedule.room,
              schedule.date,
            ].filter(Boolean).join(' ')).includes(queryText)
          : true;

        return matchTimeline && matchDistrict && matchTerminal && matchSubject && matchSearch;
      })
      .map((schedule) => {
      const scheduleDate = String(schedule.date || '').trim();
      const schoolName = String(schedule.schoolName || '').trim();
      const reportSubmitted = reportSubmittedScheduleSet.has(`${scheduleDate}|${schoolName}`);
      const terminalSchoolName = getTerminalSchoolName(schedule);

      const feedbackSubmitted = teacherFeedback.some((feedback) => {
        const sameDate =
          String(feedback.scheduleDateISO || '').trim() ===
          String(schedule.date || '').trim();
        const sameSchool =
          String(feedback.schoolName || '').trim() ===
          String(schedule.schoolName || '').trim();
        const sameSubject =
          String(feedback.subject || '').trim() === String(schedule.subject || '').trim();
        const sameTime =
          String(feedback.scheduleStartTime || '').trim() ===
          String(schedule.startTime || '').trim();

        return sameDate && sameSchool && sameSubject && sameTime;
      });

      return {
        ...schedule,
        actualSchoolName: schoolName,
        actualSchoolNames: [schoolName].filter(Boolean),
        schoolName: terminalSchoolName,
        reportSubmitted,
        feedbackSubmitted,
      };
    });

    const collapsedRows = new Map();

    rows.forEach((row) => {
      const collapseKey = [
        normalizeCompareValue(row.date),
        normalizeTime(row.startTime),
        normalizeTime(row.endTime),
        normalizeCompareValue(row.schoolName),
        normalizeCompareValue(row.daerah),
        normalizeTerminalName(row.terminal),
        normalizeCompareValue(row.subject),
        normalizeCompareValue(row.teacher),
        normalizeCompareValue(row.type),
      ].join('|');

      const existing = collapsedRows.get(collapseKey);
      if (!existing) {
        collapsedRows.set(collapseKey, {
          ...row,
          source: row.source === 'firestore' ? 'firestore' : 'static',
        });
        return;
      }

      const actualSchoolNames = new Set([
        ...(existing.actualSchoolNames || []),
        ...(row.actualSchoolNames || []),
      ].filter(Boolean));

      collapsedRows.set(collapseKey, {
        ...existing,
        id: `${existing.id}__${row.id}`,
        source: 'static',
        actualSchoolNames: [...actualSchoolNames],
        reportSubmitted: existing.reportSubmitted || row.reportSubmitted,
        feedbackSubmitted: existing.feedbackSubmitted || row.feedbackSubmitted,
      });
    });

    return [...collapsedRows.values()]
      .sort((a, b) => {
        const dateCompare = (a.date || '').localeCompare(b.date || '');
        if (dateCompare !== 0) return dateCompare;
        const timeCompare = (a.startTime || '').localeCompare(b.startTime || '');
        if (timeCompare !== 0) return timeCompare;
        return (a.schoolName || '').localeCompare(b.schoolName || '');
      });
  }, [
    adminSchedules,
    reportSubmittedScheduleSet,
    scheduleLocationScope.district,
    scheduleLocationScope.terminal,
    scheduleSearch,
    scheduleSubjectFilter,
    scheduleTimelineMode,
    teacherFeedback,
    todayISO,
  ]);

  const activeScheduleFilterCount = [
    scheduleSearch,
    scheduleDistrictFilter,
    scheduleTerminalFilter,
    scheduleSubjectFilter,
  ].filter(Boolean).length;

  const timelineMonthGroups = useMemo(() => {
    const groups = {};

    scheduleTimelineRows.forEach((row) => {
      const monthKey = row.date ? row.date.slice(0, 7) : 'no-date';
      if (!groups[monthKey]) {
        const label = row.date ? new Date(`${row.date.slice(0, 7)}-01T00:00:00`) : null;
        groups[monthKey] = {
          key: monthKey,
          label: label
            ? label.toLocaleDateString('ms-MY', { month: 'long', year: 'numeric' })
            : 'Tarikh belum ditetapkan',
          rows: []
        };
      }
      groups[monthKey].rows.push(row);
    });

    return Object.values(groups).sort((a, b) => a.key.localeCompare(b.key));
  }, [scheduleTimelineRows]);

  const scheduleRowsByDate = useMemo(() => {
    return scheduleTimelineRows.reduce((groups, schedule) => {
      const key = String(schedule.date || '').trim();
      if (!key) return groups;
      if (!groups[key]) groups[key] = [];
      groups[key].push(schedule);
      return groups;
    }, {});
  }, [scheduleTimelineRows]);

  const scheduleCalendarDays = useMemo(() => {
    const year = scheduleCalendarMonth.getFullYear();
    const month = scheduleCalendarMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    return Array.from({ length: 42 }, (_, index) => {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + index);
      const iso = toLocalISODate(date);

      return {
        date,
        iso,
        isCurrentMonth: date.getMonth() === month,
        isToday: iso === todayISO,
        isSelected: iso === scheduleCalendarDate,
        rows: scheduleRowsByDate[iso] || [],
      };
    });
  }, [scheduleCalendarDate, scheduleCalendarMonth, scheduleRowsByDate, todayISO]);

  const selectedCalendarRows = useMemo(() => {
    return scheduleRowsByDate[scheduleCalendarDate] || [];
  }, [scheduleCalendarDate, scheduleRowsByDate]);

  const teacherAttendanceReminderRows = useMemo(() => {
    const now = new Date();
    const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    const collapsed = new Map();

    adminSchedules
      .filter((schedule) => schedule.active !== false)
      .forEach((schedule) => {
        const date = String(schedule.date || '').trim();
        if (!date) return;

        const compareTime = normalizeTime(schedule.endTime || schedule.startTime || '23:59');
        const classCompleted = date < todayISO || (date === todayISO && compareTime && compareTime <= currentTime);
        if (!classCompleted) return;

        const daysLate = getIsoDateDiffDays(todayISO, date);
        if (daysLate > ATTENDANCE_REMINDER_LOOKBACK_DAYS) return;

        const schoolName = String(schedule.schoolName || '').trim();
        const subject = String(schedule.subject || '').trim();
        if (!schoolName || !subject) return;

        const hasAttendance = attendanceSubmittedClassSet.has(getClassAttendanceKey(date, schoolName, subject));
        if (hasAttendance) return;

        const collapseKey = [
          date,
          normalizeTime(schedule.startTime),
          normalizeTime(schedule.endTime),
          normalizeSubjectName(subject),
          normalizeCompareValue(schedule.teacher),
          normalizeCompareValue(schedule.daerah),
          normalizeTerminalName(schedule.terminal),
        ].join('|');

        if (!collapsed.has(collapseKey)) {
          const priority = getAttendanceReminderPriority(daysLate);
          const followupId = getAttendanceFollowupId(schedule);
          const followup = attendanceFollowups[followupId] || null;

          collapsed.set(collapseKey, {
            ...schedule,
            schoolNames: [schoolName],
            schoolCount: 1,
            followupId,
            followup,
            followupDone: Boolean(followup?.done),
            reminderDaysLate: daysLate,
            reminderPriority: priority.key,
            reminderPriorityLabel: priority.label,
            reminderPriorityTone: priority.tone,
          });
        } else {
          const existing = collapsed.get(collapseKey);
          if (!existing.schoolNames.some((name) => normalizeSchoolName(name) === normalizeSchoolName(schoolName))) {
            existing.schoolNames.push(schoolName);
            existing.schoolCount = existing.schoolNames.length;
          }
        }
      });

    return [...collapsed.values()].map((row) => ({
      ...row,
      schoolName: row.schoolNames.join(', '),
    })).sort((a, b) => {
      const dateCompare = String(b.date || '').localeCompare(String(a.date || ''));
      if (dateCompare !== 0) return dateCompare;
      return String(a.startTime || '').localeCompare(String(b.startTime || ''));
    });
  }, [adminSchedules, attendanceFollowups, attendanceSubmittedClassSet, todayISO]);

  const teacherAttendanceReminderOptions = useMemo(() => {
    const dates = new Set();
    const subjects = new Set();
    const districts = new Set();
    const terminals = new Set();

    teacherAttendanceReminderRows.forEach((row) => {
      if (row.date) dates.add(row.date);
      if (row.subject) subjects.add(row.subject);
      if (row.daerah) districts.add(row.daerah);
      if (row.terminal) terminals.add(normalizeTerminalName(row.terminal));
    });

    return {
      dates: [...dates].sort((a, b) => b.localeCompare(a)),
      subjects: [...subjects].sort(),
      districts: [...districts].sort(),
      terminals: [...terminals].sort((a, b) => {
        const aNumber = Number(String(a).match(/\d+/)?.[0] || 999);
        const bNumber = Number(String(b).match(/\d+/)?.[0] || 999);
        return aNumber - bNumber || a.localeCompare(b);
      }),
    };
  }, [teacherAttendanceReminderRows]);

  const teacherAttendanceReminderSummary = useMemo(() => {
    return teacherAttendanceReminderRows.reduce(
      (acc, row) => {
        if (row.followupDone) {
          acc.done += 1;
          return acc;
        }

        if (row.reminderPriority === 'today') acc.today += 1;
        if (row.reminderPriority === 'soon') acc.soon += 1;
        if (row.reminderPriority === 'late') acc.late += 1;
        return acc;
      },
      { today: 0, soon: 0, late: 0, done: 0 }
    );
  }, [teacherAttendanceReminderRows]);

  const filteredTeacherAttendanceReminderRows = useMemo(() => {
    const search = normalizeCompareValue(reminderSearch);

    return teacherAttendanceReminderRows.filter((row) => {
      const searchable = normalizeCompareValue([
        row.teacher,
        row.subject,
        row.schoolName,
        row.daerah,
        row.terminal,
        row.date,
      ].join(' '));

      const matchSearch = search ? searchable.includes(search) : true;
      const matchDate = reminderDateFilter ? row.date === reminderDateFilter : true;
      const matchSubject = reminderSubjectFilter ? row.subject === reminderSubjectFilter : true;
      const matchDistrict = reminderDistrictFilter ? row.daerah === reminderDistrictFilter : true;
      const matchTerminal = reminderTerminalFilter ? normalizeTerminalName(row.terminal) === reminderTerminalFilter : true;
      const matchPriority = reminderPriorityFilter
        ? reminderPriorityFilter === 'done'
          ? row.followupDone
          : !row.followupDone && row.reminderPriority === reminderPriorityFilter
        : !row.followupDone;

      return matchSearch && matchDate && matchSubject && matchDistrict && matchTerminal && matchPriority;
    });
  }, [
    reminderDateFilter,
    reminderDistrictFilter,
    reminderPriorityFilter,
    reminderSearch,
    reminderSubjectFilter,
    reminderTerminalFilter,
    teacherAttendanceReminderRows,
  ]);

  const globalAdminSearchResults = useMemo(() => {
    const search = normalizeCompareValue(globalAdminSearch);
    if (search.length < 2) return [];

    const results = [];
    const seen = new Set();
    const addResult = (item) => {
      const key = `${item.type}-${item.id}`;
      if (seen.has(key) || results.length >= 10) return;
      seen.add(key);
      results.push(item);
    };

    adminSchedules.forEach((row) => {
      const text = normalizeCompareValue([
        row.date,
        row.schoolName,
        row.actualSchoolName,
        row.teacher,
        row.subject,
        row.daerah,
        row.terminal,
      ].join(' '));
      if (!text.includes(search)) return;

      addResult({
        id: row.id || `${row.date}-${row.schoolName}-${row.subject}`,
        type: 'schedule',
        label: row.subject || 'Jadual kelas',
        meta: `${row.schoolName || '-'} / ${formatMalayDate(row.date)} / ${row.teacher || '-'}`,
        action: () => {
          setActiveAdminView('schedules');
          setScheduleSearch(row.schoolName || row.subject || row.teacher || '');
          if (row.date) {
            setScheduleCalendarDate(row.date);
            setScheduleCalendarMonth(new Date(`${row.date.slice(0, 7)}-01T00:00:00`));
          }
        },
      });
    });

    teacherAttendanceReminderRows.forEach((row) => {
      const text = normalizeCompareValue([
        row.date,
        row.schoolName,
        row.teacher,
        row.subject,
        row.daerah,
        row.terminal,
      ].join(' '));
      if (!text.includes(search)) return;

      addResult({
        id: row.followupId || `${row.date}-${row.schoolName}-${row.subject}`,
        type: 'reminder',
        label: row.followupDone ? 'Peringatan sudah dihubungi' : 'Peringatan kehadiran',
        meta: `${row.teacher || '-'} / ${row.subject || '-'} / ${row.schoolName || '-'}`,
        action: () => {
          setActiveAdminView('reports');
          setReminderSearch(row.teacher || row.schoolName || row.subject || '');
          setReminderPriorityFilter(row.followupDone ? 'done' : '');
        },
      });
    });

    studentsList.forEach((student) => {
      const text = normalizeCompareValue([
        student.fullName,
        student.email,
        student.schoolName,
        student.daerah,
        student.terminal,
      ].join(' '));
      if (!text.includes(search)) return;

      addResult({
        id: student.uid,
        type: 'student',
        label: student.fullName || 'Pelajar',
        meta: `${student.schoolName || '-'} / ${student.daerah || '-'} / ${student.terminal || '-'}`,
        action: () => {
          setActiveAdminView('performance');
          setPerformanceForm((current) => ({ ...current, studentId: student.uid }));
        },
      });
    });

    teacherReflectionRecords.forEach((record) => {
      const text = normalizeCompareValue([
        record.date,
        record.teacher,
        record.subject,
        record.schoolName,
        record.daerah,
        record.terminal,
      ].join(' '));
      if (!text.includes(search)) return;

      addResult({
        id: record.id,
        type: 'reflection',
        label: record.subject || 'Refleksi guru',
        meta: `${record.teacher || '-'} / ${record.schoolName || '-'} / ${formatMalayDate(record.date)}`,
        action: () => {
          setActiveAdminView('feedback');
          setReflectionDateFilter(record.date || '');
          setReflectionSubjectFilter(record.subject || '');
          setReflectionSchoolFilter(normalizeSchoolName(record.schoolName));
        },
      });
    });

    return results;
  }, [
    adminSchedules,
    globalAdminSearch,
    studentsList,
    teacherAttendanceReminderRows,
    teacherReflectionRecords,
  ]);

  const scheduleCalendarMonthLabel = `${ADMIN_MONTHS[scheduleCalendarMonth.getMonth()]} ${scheduleCalendarMonth.getFullYear()}`;

  const moveScheduleCalendarMonth = (direction) => {
    setScheduleCalendarMonth((current) => {
      const next = new Date(current);
      next.setMonth(current.getMonth() + direction);
      return next;
    });
  };

  const goScheduleCalendarToday = () => {
    setHasAnchoredScheduleCalendar(true);
    setScheduleCalendarMonth(new Date(`${todayISO.slice(0, 7)}-01T00:00:00`));
    setScheduleCalendarDate(todayISO);
  };

  useEffect(() => {
    if (hasAnchoredScheduleCalendar) return;
    if (!scheduleTimelineRows.length) return;
    if (scheduleRowsByDate[scheduleCalendarDate]) return;
    if (scheduleCalendarDate !== todayISO) return;

    const anchorDate =
      scheduleTimelineRows.find((row) => String(row.date || '') >= todayISO)?.date ||
      scheduleTimelineRows[0]?.date;

    if (anchorDate) {
      setScheduleCalendarDate(anchorDate);
      setScheduleCalendarMonth(new Date(`${anchorDate.slice(0, 7)}-01T00:00:00`));
      setHasAnchoredScheduleCalendar(true);
    }
  }, [hasAnchoredScheduleCalendar, scheduleCalendarDate, scheduleRowsByDate, scheduleTimelineRows, todayISO]);

  const schoolPerformance = useMemo(() => {
    const groups = {};

    filteredAttendanceRecords.forEach((record) => {
      if (!groups[record.schoolName]) {
        groups[record.schoolName] = {
          schoolName: record.schoolName,
          daerah: record.daerah,
          terminal: record.terminal,
          totalPresent: 0,
          totalStudents: 0,
          totalSessions: 0,
        };
      }

      groups[record.schoolName].totalPresent += record.present;
      groups[record.schoolName].totalStudents += record.total;
      groups[record.schoolName].totalSessions += 1;
    });

    return Object.values(groups)
      .map((school) => {
        const percent = school.totalStudents
          ? Math.round((school.totalPresent / school.totalStudents) * 100)
          : 0;

        return {
          ...school,
          averagePresent: school.totalSessions
            ? Number((school.totalPresent / school.totalSessions).toFixed(1))
            : 0,
          averageStudents: school.totalSessions
            ? Number((school.totalStudents / school.totalSessions).toFixed(1))
            : 0,
          percent,
          status: getStatusLabel(percent),
        };
      })
      .sort((a, b) => a.percent - b.percent);
  }, [filteredAttendanceRecords]);

  const attendanceSubjectMatrix = useMemo(() => {
    const groups = {};

    filteredAttendanceRecords.forEach((record) => {
      if (!record.date || !record.schoolName || record.subject === 'LAIN' || !record.total) return;

      const key = `${record.date}|${record.schoolName}`;
      if (!groups[key]) {
        groups[key] = {
          key,
          date: record.date,
          schoolName: record.schoolName,
          daerah: record.daerah,
          terminal: record.terminal,
          subjects: PERFORMANCE_SUBJECTS.reduce((acc, subject) => {
            acc[subject] = {
              present: 0,
              total: 0,
              percent: null,
              sessions: 0,
            };
            return acc;
          }, {}),
        };
      }

      if (!groups[key].subjects[record.subject]) return;

      groups[key].subjects[record.subject].present += record.present;
      groups[key].subjects[record.subject].total += record.total;
      groups[key].subjects[record.subject].sessions += 1;
    });

    return Object.values(groups)
      .map((group) => ({
        ...group,
        subjects: Object.fromEntries(
          Object.entries(group.subjects).map(([subject, value]) => [
            subject,
            {
              ...value,
              percent: value.total ? Math.round((value.present / value.total) * 100) : null,
            },
          ])
        ),
      }))
      .sort((a, b) => {
        const dateCompare = (b.date || '').localeCompare(a.date || '');
        if (dateCompare !== 0) return dateCompare;
        return (a.schoolName || '').localeCompare(b.schoolName || '');
      });
  }, [filteredAttendanceRecords]);

  const schoolsBelowKPI = schoolPerformance.filter((s) => s.percent < KPI_TARGET);

  const activeSchoolsOnDate = Object.keys(groupedSchedules).length;
  const totalClassesOnDate = filteredSchedules.length;

  const teacherReflectionRate = useMemo(() => {
    if (!teacherReflectionSummary.total) return 0;
    return Math.round((teacherReflectionSummary.objectiveAchieved / teacherReflectionSummary.total) * 100);
  }, [teacherReflectionSummary.objectiveAchieved, teacherReflectionSummary.total]);

  const criticalSchools = useMemo(() => {
    return [...schoolsBelowKPI].sort((a, b) => a.percent - b.percent).slice(0, 10);
  }, [schoolsBelowKPI]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      console.error('logout error:', error);
      showToast('bad', 'Logout gagal.');
    }
  };

  const resetScheduleForm = () => {
    setScheduleForm(EMPTY_SCHEDULE_FORM);
    setEditingScheduleId('');
    setScheduleEditorOpen(false);
  };

  const handleScheduleFormChange = (field, value) => {
    const selectedManualSchool =
      field === 'schoolName'
        ? MANUAL_SCHOOL_OPTIONS.find((school) => school.name === value)
        : null;

    setScheduleForm((current) => ({
      ...current,
      [field]: value,
      ...(selectedManualSchool
        ? {
            daerah: selectedManualSchool.daerah,
            terminal: selectedManualSchool.terminal,
          }
        : {}),
      ...(field === 'subject' ? { type: inferActivityType(value) } : {})
    }));
  };

  const handleEditSchedule = (schedule) => {
    if (schedule.source !== 'firestore') {
      showToast('bad', 'Jadual asal import belum boleh edit. Add jadual baru di Firestore dulu.');
      return;
    }

    setEditingScheduleId(schedule.id);
    setScheduleEditorOpen(true);
    setScheduleForm({
      date: schedule.date || '',
      startTime: schedule.startTime || '',
      endTime: schedule.endTime || '',
      schoolName: schedule.schoolName || '',
      daerah: schedule.daerah || '',
      terminal: schedule.terminal || 'Terminal 1',
      subject: schedule.subject || '',
      teacher: schedule.teacher || '',
      type: schedule.type || 'IVC',
      room: schedule.room || '',
      active: schedule.active !== false,
    });
  };

  const handleSaveSchedule = async (event) => {
    event.preventDefault();

    const requiredFields = ['date', 'startTime', 'endTime', 'schoolName', 'daerah', 'terminal', 'subject', 'teacher', 'type'];
    const missingField = requiredFields.find((field) => !String(scheduleForm[field] || '').trim());

    if (missingField) {
      showToast('bad', 'Lengkapkan semua field wajib jadual dulu.');
      return;
    }

    try {
      setSavingSchedule(true);

      const payload = {
        date: scheduleForm.date,
        dayOfWeek: getDayOfWeek(scheduleForm.date),
        startTime: normalizeTime(scheduleForm.startTime),
        endTime: normalizeTime(scheduleForm.endTime),
        schoolName: scheduleForm.schoolName.trim(),
        daerah: scheduleForm.daerah.trim(),
        terminal: scheduleForm.terminal.trim(),
        subject: scheduleForm.subject.trim(),
        teacher: scheduleForm.teacher.trim(),
        type: scheduleForm.type.trim(),
        room: scheduleForm.room.trim(),
        active: Boolean(scheduleForm.active),
        updatedAt: serverTimestamp(),
      };

      if (editingScheduleId) {
        await updateDoc(doc(db, 'schedules', editingScheduleId), payload);
        setFirestoreSchedules((current) =>
          current.map((item) =>
            item.id === editingScheduleId ? { ...item, ...payload, source: 'firestore' } : item
          )
        );
        showToast('ok', 'Jadual berjaya dikemaskini.');
      } else {
        const docRef = await addDoc(collection(db, 'schedules'), {
          ...payload,
          createdAt: serverTimestamp(),
        });
        setFirestoreSchedules((current) =>
          [...current, { id: docRef.id, ...payload, source: 'firestore' }].sort((a, b) => {
            const dateCompare = (a.date || '').localeCompare(b.date || '');
            if (dateCompare !== 0) return dateCompare;
            return (a.startTime || '').localeCompare(b.startTime || '');
          })
        );
        showToast('ok', 'Jadual baru berjaya ditambah.');
      }

      resetScheduleForm();
    } catch (error) {
      console.error('handleSaveSchedule error:', error);
      showToast('bad', 'Gagal simpan jadual. Semak kebenaran pentadbir.');
    } finally {
      setSavingSchedule(false);
    }
  };

  const handleDeleteSchedule = async (schedule) => {
    if (schedule.source !== 'firestore') {
      showToast('bad', 'Jadual asal import tak boleh delete dari UI ini.');
      return;
    }

    const ok = window.confirm(`Padam jadual ${schedule.subject || 'kelas'} di ${schedule.schoolName || 'sekolah ini'}?`);
    if (!ok) return;

    try {
      await deleteDoc(doc(db, 'schedules', schedule.id));
      setFirestoreSchedules((current) => current.filter((item) => item.id !== schedule.id));
      if (editingScheduleId === schedule.id) resetScheduleForm();
      showToast('ok', 'Jadual berjaya dipadam.');
    } catch (error) {
      console.error('handleDeleteSchedule error:', error);
      showToast('bad', 'Gagal delete jadual.');
    }
  };

  const handlePerformanceFormChange = (field, value) => {
    setPerformanceForm((current) => ({
      ...current,
      [field]: value
    }));
  };

  const handleSavePerformance = async (event) => {
    event.preventDefault();

    if (!performanceForm.studentId) {
      showToast('bad', 'Pilih pelajar dulu.');
      return;
    }

    const selectedStudent = studentsList.find((student) => student.uid === performanceForm.studentId);
    if (!selectedStudent) {
      showToast('bad', 'Pelajar tidak dijumpai.');
      return;
    }

    try {
      setSavingPerformance(true);

      const docId = `${performanceForm.studentId}_${performanceForm.subject}`;
      const baselineMark = toOptionalMark(performanceForm.baselineMark);
      const currentMark = toOptionalMark(performanceForm.currentMark);
      const performancePayload = {
        studentId: performanceForm.studentId,
        studentName: selectedStudent.fullName || '',
        studentEmail: selectedStudent.email || '',
        subject: performanceForm.subject,
        baselineGrade: performanceForm.baselineGrade,
        baselineMark,
        baselineMarkEntered: baselineMark !== null,
        currentGrade: performanceForm.currentGrade,
        currentMark,
        currentMarkEntered: currentMark !== null,
        examLabel: performanceForm.examLabel || 'Rekod semasa',
        updatedAt: serverTimestamp(),
        updatedBy: auth.currentUser?.uid || '',
      };

      await setDoc(doc(db, 'student_performance', docId), performancePayload, { merge: true });
      setPerformanceRecords((current) => [
        { id: docId, ...performancePayload, updatedAt: new Date().toISOString() },
        ...current.filter((record) => record.id !== docId),
      ]);

      showToast('ok', 'Prestasi pelajar berjaya disimpan.');
      setPerformanceForm((current) => ({
        ...EMPTY_PERFORMANCE_FORM,
        studentId: current.studentId,
        subject: current.subject,
      }));
    } catch (error) {
      console.error('handleSavePerformance error:', error);
      showToast('bad', 'Gagal simpan prestasi pelajar.');
    } finally {
      setSavingPerformance(false);
    }
  };

  const handleDownloadPerformanceTemplate = () => {
    downloadCsv(
      'templat-import-prestasi.csv',
      [
        { label: 'E-mel Pelajar', value: (row) => row.email },
        { label: 'Nama Pelajar', value: (row) => row.name },
        { label: 'Subjek', value: (row) => row.subject },
        { label: 'Baseline Gred', value: (row) => row.baselineGrade },
        { label: 'Baseline Markah', value: (row) => row.baselineMark },
        { label: 'Gred Terkini', value: (row) => row.currentGrade },
        { label: 'Markah Terkini', value: (row) => row.currentMark },
        { label: 'Nama Ujian', value: (row) => row.examLabel },
      ],
      [{
        email: 'pelajar@contoh.com',
        name: 'Nama Pelajar',
        subject: 'BM',
        baselineGrade: 'D',
        baselineMark: 45,
        currentGrade: 'C',
        currentMark: 58,
        examLabel: 'Ujian 1',
      }]
    );
  };

  const handleImportPerformance = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    try {
      setImportingPerformance(true);
      const parsed = Papa.parse(await file.text(), { header: true, skipEmptyLines: true });
      if (parsed.errors?.length) {
        throw new Error('Fail CSV tidak dapat dibaca dengan sempurna.');
      }

      const studentsByEmail = new Map(studentsList.map((student) => [String(student.email || '').trim().toLowerCase(), student]));
      const studentsByName = new Map(studentsList.map((student) => [normalizePersonKey(student.fullName), student]));
      const importedRecords = [];
      let skipped = 0;

      for (const row of parsed.data || []) {
        const email = getCsvField(row, ['E-mel Pelajar', 'Email Pelajar', 'Email']);
        const name = getCsvField(row, ['Nama Pelajar', 'Nama Penuh', 'Student Name']);
        const student = studentsByEmail.get(email.toLowerCase()) || studentsByName.get(normalizePersonKey(name));
        const subject = normalizeSubjectKey(getCsvField(row, ['Subjek', 'Subject']));
        const baselineGrade = normalizeGrade(getCsvField(row, ['Baseline Gred', 'Gred Baseline', 'Baseline Grade']));
        const currentGrade = normalizeGrade(getCsvField(row, ['Gred Terkini', 'Current Grade', 'Gred Semasa']));

        if (!student || !PERFORMANCE_SUBJECTS.includes(subject) || !PERFORMANCE_GRADES.includes(baselineGrade) || !PERFORMANCE_GRADES.includes(currentGrade)) {
          skipped += 1;
          continue;
        }

        const baselineMark = toOptionalMark(getCsvField(row, ['Baseline Markah', 'Markah Baseline', 'Baseline Mark']));
        const currentMark = toOptionalMark(getCsvField(row, ['Markah Terkini', 'Current Mark', 'Markah Semasa']));
        const examLabel = getCsvField(row, ['Nama Ujian', 'Ujian', 'Exam', 'Exam Name']) || 'Import CSV';
        const docId = `${student.uid}_${subject}`;
        const payload = {
          studentId: student.uid,
          studentName: student.fullName || name,
          studentEmail: student.email || email,
          subject,
          baselineGrade,
          baselineMark,
          baselineMarkEntered: baselineMark !== null,
          currentGrade,
          currentMark,
          currentMarkEntered: currentMark !== null,
          examLabel,
          source: 'csv_import',
          updatedAt: serverTimestamp(),
          updatedBy: auth.currentUser?.uid || '',
        };

        await setDoc(doc(db, 'student_performance', docId), payload, { merge: true });
        importedRecords.push({ id: docId, ...payload, updatedAt: new Date().toISOString() });
      }

      if (!importedRecords.length) {
        throw new Error('Tiada rekod sah dijumpai. Semak nama/e-mel pelajar, subjek dan gred dalam templat.');
      }

      setPerformanceRecords((current) => {
        const importedIds = new Set(importedRecords.map((record) => record.id));
        return [...importedRecords, ...current.filter((record) => !importedIds.has(record.id))];
      });
      showToast('ok', `${importedRecords.length} rekod berjaya diimport${skipped ? `, ${skipped} baris dilangkau` : ''}.`);
    } catch (error) {
      console.error('handleImportPerformance error:', error);
      showToast('bad', error.message || 'Gagal import prestasi daripada CSV.');
    } finally {
      setImportingPerformance(false);
    }
  };

  const handleExportSchedules = () => {
    downloadCsv(
      `btop-jadual-${todayISO}.csv`,
      [
        { label: 'Tarikh', value: (row) => row.date || '' },
        { label: 'Masa Mula', value: (row) => row.startTime || '' },
        { label: 'Masa Tamat', value: (row) => row.endTime || '' },
        { label: 'Sekolah Terminal', value: (row) => row.schoolName || '' },
        { label: 'Sekolah Terlibat', value: (row) => (row.actualSchoolNames || [row.actualSchoolName]).filter(Boolean).join(' | ') },
        { label: 'Daerah', value: (row) => row.daerah || '' },
        { label: 'Terminal', value: (row) => row.terminal || '' },
        { label: 'Subjek', value: (row) => row.subject || '' },
        { label: 'Cikgu', value: (row) => row.teacher || '' },
        { label: 'Jenis', value: (row) => row.type || '' },
        { label: 'Laporan', value: (row) => (row.reportSubmitted ? 'Dihantar' : 'Belum hantar') },
        { label: 'Maklum Balas', value: (row) => (row.feedbackSubmitted ? 'Ada' : 'Belum ada') },
      ],
      scheduleTimelineRows
    );
  };

  const handleExportAttendance = () => {
    downloadCsv(
      `btop-kehadiran-${todayISO}.csv`,
      [
        { label: 'Tarikh', value: (row) => row.date || '' },
        { label: 'Sekolah', value: (row) => row.schoolName || '' },
        { label: 'Daerah', value: (row) => row.daerah || '' },
        { label: 'Terminal', value: (row) => row.terminal || '' },
        { label: 'BM', value: (row) => row.subjects.BM?.percent ?? '' },
        { label: 'BI', value: (row) => row.subjects.BI?.percent ?? '' },
        { label: 'MATH', value: (row) => row.subjects.MATH?.percent ?? '' },
        { label: 'SEJARAH', value: (row) => row.subjects.SEJARAH?.percent ?? '' },
      ],
      attendanceSubjectMatrix
    );
  };

  const handleExportSchoolPerformance = () => {
    downloadCsv(
      `btop-prestasi-kehadiran-${todayISO}.csv`,
      [
        { label: 'Sekolah', value: (row) => row.schoolName || '' },
        { label: 'Daerah', value: (row) => row.daerah || '' },
        { label: 'Terminal', value: (row) => row.terminal || '' },
        { label: 'Purata Hadir Per Sesi', value: (row) => row.averagePresent || 0 },
        { label: 'Purata Pelajar Per Sesi', value: (row) => row.averageStudents || 0 },
        { label: 'Jumlah Rekod Kehadiran', value: (row) => row.totalSessions || 0 },
        { label: 'Jumlah Kehadiran Terkumpul', value: (row) => row.totalPresent || 0 },
        { label: 'Jumlah Peluang Kehadiran', value: (row) => row.totalStudents || 0 },
        { label: 'Peratus Kehadiran', value: (row) => `${row.percent || 0}%` },
        { label: 'Status', value: (row) => row.status || '' },
      ],
      schoolPerformance
    );
  };

  const getReminderMessage = (row) => {
    const teacherName = row.teacher || 'cikgu';
    const timeText = [
      row.startTime ? formatDisplayTime(row.startTime) : '',
      row.endTime ? formatDisplayTime(row.endTime) : '',
    ].filter(Boolean).join(' - ');
    return [
      `Assalamualaikum ${teacherName},`,
      '',
      `Mohon kemaskini rekod kehadiran untuk kelas ${row.subject || 'subjek'} pada ${formatMalayDate(row.date)}${timeText ? ` (${timeText})` : ''}.`,
      `Sekolah: ${row.schoolName || '-'}`,
      `Daerah/Terminal: ${row.daerah || '-'} / ${row.terminal || '-'}`,
      '',
      'Terima kasih.',
    ].join('\n');
  };

  const getTeacherPhone = (row) => {
    const target = normalizePersonKey(row.teacher);
    const profile = teacherProfiles.find((teacher) => {
      const name = normalizePersonKey(teacher.fullName);
      return target && name && (target === name || target.includes(name) || name.includes(target));
    });
    return String(row.teacherPhone || profile?.phone || '').replace(/[^\d]/g, '').replace(/^0/, '60');
  };

  const handleOpenReminderWhatsApp = (row) => {
    const phone = getTeacherPhone(row);
    if (!phone) {
      showToast('bad', 'Nombor telefon cikgu belum ada dalam profil. Gunakan Salin Mesej dahulu.');
      return;
    }
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(getReminderMessage(row))}`, '_blank', 'noopener,noreferrer');
  };

  const handleCopyReminderMessage = async (row) => {
    const message = getReminderMessage(row);

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(message);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = message;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        textarea.remove();
      }

      showToast('ok', 'Mesej peringatan berjaya disalin.');
    } catch (error) {
      console.error('handleCopyReminderMessage error:', error);
      showToast('bad', 'Gagal salin mesej peringatan.');
    }
  };

  const handleToggleReminderFollowup = async (row, done = true) => {
    const followupId = row.followupId || getAttendanceFollowupId(row);
    const payload = {
      done,
      date: row.date || '',
      startTime: row.startTime || '',
      endTime: row.endTime || '',
      schoolName: row.schoolName || '',
      daerah: row.daerah || '',
      terminal: row.terminal || '',
      subject: row.subject || '',
      teacher: row.teacher || '',
      updatedAt: serverTimestamp(),
      updatedBy: auth.currentUser?.uid || '',
      updatedByEmail: auth.currentUser?.email || '',
    };

    try {
      await setDoc(doc(db, 'attendance_followups', followupId), payload, { merge: true });
      setAttendanceFollowups((current) => ({
        ...current,
        [followupId]: {
          ...(current[followupId] || {}),
          id: followupId,
          ...payload,
          updatedAtLocal: new Date().toISOString(),
        },
      }));
      showToast('ok', done ? 'Peringatan ditanda sudah dihubungi.' : 'Peringatan dibuka semula.');
    } catch (error) {
      console.error('handleToggleReminderFollowup error:', error);
      showToast('bad', 'Gagal kemaskini status peringatan.');
    }
  };

  const adminNavItems = [
    { key: 'overview', label: 'Utama', onClick: () => setActiveAdminView('overview') },
    { key: 'schedules', label: 'Jadual', onClick: () => {
      setActiveAdminView('schedules');
      setScheduleDisplayMode('calendar');
    } },
    { key: 'performance', label: 'Prestasi', onClick: () => setActiveAdminView('performance') },
    { key: 'feedback', label: 'Maklum Balas', onClick: () => setActiveAdminView('feedback') },
    { key: 'reports', label: 'Laporan', onClick: () => setActiveAdminView('reports') },
  ];

  if (loading) {
    return (
      <div className="admin-screen">
        <div className="admin-loader">
          <div className="admin-spinner" />
          <p>Memuatkan papan pemuka pentadbir...</p>
        </div>
      </div>
    );
  }

  return (
    <DashboardShell
      brand="BTOP Wellness"
      subtitle="Papan Pemuka Pentadbir"
      roleLabel="Pentadbir"
      userName={adminData?.fullName}
      userInitial={adminData?.fullName?.charAt(0)?.toUpperCase() || 'A'}
      navItems={adminNavItems}
      activeKey={activeAdminView}
      onLogout={handleLogout}
      tone="gold"
    >
    <div className="admin-screen">
      {toast.show && (
        <div className={`admin-toast ${toast.type}`}>
          <span className={`ico toast-ico ${toast.type === 'ok' ? 'toast-ico-ok' : 'toast-ico-bad'}`} aria-hidden="true" />
          <span>{toast.msg}</span>
        </div>
      )}

      <header className="admin-topbar">
        <div className="admin-topbar-left">
          <div className="admin-appicon" aria-hidden="true"><span /></div>
          <div className="admin-titles">
            <div className="admin-title">BEST 2.0</div>
            <div className="admin-subtitle">Papan Pemuka Pentadbir</div>
          </div>
        </div>

        <div className="admin-global-search">
          <label htmlFor="admin-global-search-input">Carian pantas</label>
          <input
            id="admin-global-search-input"
            type="search"
            value={globalAdminSearch}
            onChange={(event) => setGlobalAdminSearch(event.target.value)}
            placeholder="Cari sekolah, cikgu, pelajar atau subjek"
          />
          {globalAdminSearch.trim().length >= 2 && (
            <div className="admin-global-results">
              {globalAdminSearchResults.length === 0 ? (
                <div className="admin-global-empty">Tiada padanan dijumpai.</div>
              ) : (
                globalAdminSearchResults.map((result) => (
                  <button
                    key={`${result.type}-${result.id}`}
                    type="button"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => {
                      result.action();
                      setGlobalAdminSearch('');
                    }}
                  >
                    <span>{result.type === 'schedule'
                      ? 'Jadual'
                      : result.type === 'reminder'
                      ? 'Peringatan'
                      : result.type === 'student'
                      ? 'Pelajar'
                      : 'Refleksi'}</span>
                    <strong>{result.label}</strong>
                    <small>{result.meta}</small>
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        <div className="admin-topbar-right">
          <div className="admin-avatar">
            {adminData?.fullName?.charAt(0)?.toUpperCase() || 'A'}
          </div>
          <button className="admin-logout" type="button" onClick={handleLogout}>
            Keluar
          </button>
        </div>
      </header>

      <main className="admin-content">
        <section className="admin-hero">
          <div className="admin-hero-card">
            <div className="admin-hero-text">
              <div className="admin-hero-kicker">{getGreeting()}</div>
              <h2 className="admin-hero-title">{adminData?.fullName || 'Pentadbir'} </h2>
              <p className="admin-hero-desc">
                Pantau jadual, laporan dan kehadiran sekolah dalam satu paparan ringkas.
              </p>

              <div className="admin-pillrow">
                <span className="admin-pill">Tarikh {formatMalayDate(selectedDate)}</span>
                <span className="admin-pill">{activeSchoolsOnDate} sekolah aktif</span>
                <span className="admin-pill">{totalClassesOnDate} kelas</span>
                <span className="admin-pill">{pendingReportSchools.length} belum laporan</span>
              </div>
            </div>
          </div>
        </section>

        {activeAdminView === 'overview' && (
        <>
        <section className="admin-section admin-filter-section">
          <div className="admin-section-head">
            <div className="admin-section-titleblock">
              <h3>Penapis</h3>
              <div className="admin-view-meta admin-filter-help">
                <span className="admin-filter-help-desktop">Pilih sekolah dahulu untuk kira sesi kehadiran dengan lebih tepat.</span>
                <span className="admin-filter-help-mobile">Pilih sekolah dan sesi untuk semak kehadiran.</span>
              </div>
            </div>
          </div>

          <div className="admin-filters admin-filters-guided">
            <button
              className="admin-mobile-filter-toggle"
              type="button"
              aria-expanded={showMobileLocationFilters}
              onClick={() => setShowMobileLocationFilters((current) => !current)}
            >
              {showMobileLocationFilters ? 'Tutup penapis tambahan' : 'Penapis tambahan'}
            </button>
            <div className={`admin-location-filters ${showMobileLocationFilters ? 'is-open' : ''}`}>
              <div className="admin-field admin-filter-district">
                <label>Daerah</label>
                <select
                  value={selectedDistrict}
                  onChange={(e) => {
                    setSelectedDistrict(e.target.value);
                    setSelectedTerminal('');
                    setSelectedSchool('');
                    setSelectedAttendanceSession('');
                  }}
                >
                  <option value="">Semua daerah</option>
                  {districtOptions.map((district) => (
                    <option key={district} value={district}>
                      {district}
                    </option>
                  ))}
                </select>
              </div>

              <div className="admin-field admin-filter-terminal">
                <label>Terminal</label>
                <select
                  value={selectedTerminal}
                  onChange={(e) => {
                    setSelectedTerminal(e.target.value);
                    setSelectedSchool('');
                    setSelectedAttendanceSession('');
                  }}
                >
                  <option value="">Semua terminal</option>
                  {terminalOptions.map((terminal) => (
                    <option key={terminal} value={terminal}>
                      {terminal}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="admin-field admin-filter-school">
              <label>Sekolah</label>
              <select
                value={selectedSchool}
                onChange={(e) => {
                  const school = e.target.value;
                  const meta = getManualSchoolMeta(school);
                  setSelectedSchool(school);
                  if (school) {
                    setSelectedDistrict(meta.daerah);
                    setSelectedTerminal(meta.terminal);
                  }
                }}
              >
                <option value="">Pilih sekolah</option>
                {overviewSchoolOptions.map((school) => (
                  <option key={school} value={school}>
                    {school}
                  </option>
                ))}
              </select>
            </div>

            <div className="admin-field admin-filter-scope">
              <label>Skop aktif</label>
              <input
                value={selectedSchool ? `${selectedSchoolMeta.daerah} / ${selectedSchoolMeta.terminal}` : 'Pilih sekolah untuk sesi'}
                disabled
                readOnly
              />
            </div>

            <div className="admin-field admin-filter-session">
              <label>Sesi Kehadiran</label>
              <select
                value={selectedAttendanceSession}
                onChange={(e) => setSelectedAttendanceSession(e.target.value)}
                disabled={!selectedSchool || attendanceSessionOptions.length === 0}
              >
                {!selectedSchool ? (
                  <option value="">Pilih sekolah dulu</option>
                ) : attendanceSessionOptions.length === 0 ? (
                  <option value="">Tiada data sesi</option>
                ) : (
                  attendanceSessionOptions.map((session) => (
                    <option key={session} value={session}>
                      {getAttendanceSessionLabel(session)}
                    </option>
                  ))
                )}
              </select>
            </div>
          </div>
        </section>

        <section className="admin-section">
          <div className="admin-section-head">
            <div className="admin-section-titleblock">
              <h3>Kehadiran Mengikut Sesi</h3>
              <div className="admin-view-meta">
                Pilih sekolah, sistem akan mengesan terminal dan mengira peratus kehadiran subjek mengikut sesi.
              </div>
            </div>
            <span className="admin-subpill">
              {selectedAttendanceSession
                ? `${selectedAttendanceSession}${selectedAttendanceSessionDateLabel ? ` / ${selectedAttendanceSessionDateLabel}` : ''}`
                : 'Sesi belum dipilih'}
            </span>
          </div>

          <div className="admin-attendance-legend">
            <span className="good">Hijau 90%+</span>
            <span className="warn">Kuning 75-89%</span>
            <span className="bad">Merah &lt;75%</span>
            <small>
              {loadingSheet
                ? 'Sedang menyelaras Google Sheet...'
                : selectedSchool
                ? `${overviewAttendanceHealth.good} baik / ${overviewAttendanceHealth.warn} perhatian / ${overviewAttendanceHealth.bad} kritikal / ${overviewAttendanceHealth.empty} belum lengkap`
                : 'Pilih sekolah untuk semak status subjek.'}
            </small>
          </div>

          {loadingSheet ? (
            <div className="admin-card admin-empty-inline admin-state-card">
              <div className="admin-state-dot loading" />
              <div>
                <strong>Sedang menyelaras Google Sheet</strong>
                <p>Data kehadiran akan dipaparkan ikut sekolah, sesi dan subjek bila CSV selesai dibaca.</p>
              </div>
            </div>
          ) : !selectedSchool ? (
            <div className="admin-card admin-empty-inline">
              Pilih satu sekolah untuk paparkan kehadiran BM, BI, MATH dan SEJ mengikut terminal serta sesi.
            </div>
          ) : (
            <div className="admin-session-subject-grid">
              {overviewSubjectAttendance.map((item) => {
                const hasData = item.percent !== null;
                return (
                  <article
                    key={item.subject}
                    className={`admin-session-subject-card ${hasData ? getAttendanceTone(item.percent) : 'empty'}`}
                  >
                    <div>
                      <span>{item.subject}</span>
                      <strong>{hasData ? `${item.percent}%` : '-'}</strong>
                    </div>
                    <p>
                      {hasData
                        ? `${item.present}/${item.total} hadir / ${item.date ? formatMalayDate(item.date) : 'tarikh rekod belum ada'}`
                        : 'Tiada rekod kehadiran lengkap'}
                    </p>
                    {hasData && item.absentBySchool.length > 0 && (
                      <div className="admin-absent-list">
                        <small>Tidak hadir</small>
                        {item.absentBySchool.map((group) => (
                          <div key={group.schoolName} className="admin-absent-school">
                            <b>{group.schoolName}</b>
                            <em>{group.students.length} nama</em>
                            <ul>
                              {group.students.map((student) => (
                                <li key={student.id}>{student.name}</li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    )}
                    {hasData && item.absentBySchool.length === 0 && item.absentNote && (
                      <div className="admin-absent-list">
                        <small>Catatan tidak hadir</small>
                        <p className="admin-absent-note">{item.absentNote}</p>
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          )}
        </section>

        </>
        )}

        {activeAdminView === 'schedules' && (
        <section className="admin-section">
          <div className="admin-section-head">
              <div className="admin-section-titleblock">
                <h3>Semua Jadual Kelas</h3>
                <div className="admin-view-meta">
                {scheduleTimelineRows.length} kelas dipaparkan / sekolah terminal sahaja
                {activeScheduleFilterCount ? ` / ${activeScheduleFilterCount} penapis aktif` : ''}
                </div>
              </div>
            <div className="admin-section-actions">
              <span className="admin-subpill">
                {scheduleTimelineMode === 'all'
                  ? 'Semua tarikh'
                  : scheduleTimelineMode === 'today'
                  ? 'Hari ini'
                  : scheduleTimelineMode === 'upcoming'
                  ? 'Akan datang'
                  : 'Lepas'}
              </span>
              <button className="admin-export-btn" type="button" onClick={handleExportSchedules}>
                Muat Turun CSV
              </button>
            </div>
          </div>

          <div className="admin-view-toggle admin-timeline-toggle">
            <button
              type="button"
              onClick={() => setScheduleTimelineMode('all')}
              className={`admin-view-toggle-btn ${scheduleTimelineMode === 'all' ? 'active all' : ''}`}
            >
              Semua
            </button>
            <button
              type="button"
              onClick={() => setScheduleTimelineMode('today')}
              className={`admin-view-toggle-btn ${scheduleTimelineMode === 'today' ? 'active all' : ''}`}
            >
              Hari ini
            </button>
            <button
              type="button"
              onClick={() => setScheduleTimelineMode('upcoming')}
              className={`admin-view-toggle-btn ${scheduleTimelineMode === 'upcoming' ? 'active upcoming' : ''}`}
            >
              Akan datang
            </button>
            <button
              type="button"
              onClick={() => setScheduleTimelineMode('past')}
              className={`admin-view-toggle-btn ${scheduleTimelineMode === 'past' ? 'active pending' : ''}`}
            >
              Lepas
            </button>
          </div>

          <div className="admin-schedule-filterbar">
            <div className="admin-schedule-search">
              <label>Cari jadual</label>
              <input
                type="text"
                value={scheduleSearch}
                onChange={(event) => setScheduleSearch(event.target.value)}
                placeholder="Sekolah, subjek, cikgu, tarikh..."
              />
            </div>

            <div className="admin-schedule-filter">
              <label>Daerah</label>
              <select
                value={scheduleDistrictFilter}
                onChange={(event) => setScheduleDistrictFilter(event.target.value)}
              >
                <option value="">Semua daerah</option>
                {scheduleFilterOptions.districts.map((district) => (
                  <option key={district} value={district}>{district}</option>
                ))}
              </select>
            </div>

            <div className="admin-schedule-filter">
              <label>Terminal</label>
              <select
                value={scheduleTerminalFilter}
                onChange={(event) => setScheduleTerminalFilter(event.target.value)}
              >
                <option value="">Semua terminal</option>
                {scheduleFilterOptions.terminals.map((terminal) => (
                  <option key={terminal} value={terminal}>{terminal}</option>
                ))}
              </select>
            </div>

            <div className="admin-schedule-filter">
              <label>Subjek</label>
              <select
                value={scheduleSubjectFilter}
                onChange={(event) => setScheduleSubjectFilter(event.target.value)}
              >
                <option value="">Semua subjek</option>
                {scheduleFilterOptions.subjects.map((subject) => (
                  <option key={subject} value={subject}>{subject}</option>
                ))}
              </select>
            </div>

            <button
              className="admin-schedule-filter-reset"
              type="button"
              onClick={resetScheduleFilters}
              disabled={!activeScheduleFilterCount}
            >
              Reset
            </button>
          </div>

          <div className="admin-view-toggle admin-layout-toggle">
            <button
              type="button"
              onClick={() => setScheduleDisplayMode('calendar')}
              className={`admin-view-toggle-btn ${scheduleDisplayMode === 'calendar' ? 'active all' : ''}`}
            >
              Kalendar
            </button>
            <button
              type="button"
              onClick={() => setScheduleDisplayMode('list')}
              className={`admin-view-toggle-btn ${scheduleDisplayMode === 'list' ? 'active upcoming' : ''}`}
            >
              Senarai
            </button>
          </div>

          <div className="admin-schedule-editor-launch">
            <div>
              <strong>{editingScheduleId ? 'Sedang kemas kini jadual' : 'Urus jadual kelas'}</strong>
              <span>{scheduleEditorOpen ? 'Lengkapkan maklumat di bawah.' : 'Buka borang hanya apabila mahu menambah jadual baharu.'}</span>
            </div>
            <button
              type="button"
              onClick={() => {
                if (scheduleEditorOpen) resetScheduleForm();
                else setScheduleEditorOpen(true);
              }}
            >
              {scheduleEditorOpen ? 'Tutup' : 'Tambah Jadual'}
            </button>
          </div>

          {scheduleEditorOpen && (
          <form className="admin-schedule-editor" onSubmit={handleSaveSchedule}>
            <div className="admin-section-head admin-editor-head">
              <div className="admin-section-titleblock">
                <h3>{editingScheduleId ? 'Kemaskini Jadual' : 'Tambah Jadual Baru'}</h3>
                <div className="admin-view-meta">
                  Jadual yang disimpan di sini terus masuk Firestore dan boleh dibaca oleh cikgu/pelajar.
                </div>
              </div>
              <span className="admin-subpill">
                {loadingSchedulesAdmin ? 'Memuatkan jadual...' : `${firestoreSchedules.length} boleh dikemaskini`}
              </span>
            </div>

            <div className="admin-editor-grid">
              <div className="admin-field schedule-field-date">
                <label>Tarikh *</label>
                <input
                  type="date"
                  value={scheduleForm.date}
                  onChange={(event) => handleScheduleFormChange('date', event.target.value)}
                />
              </div>

              <div className="admin-field schedule-field-start">
                <label>Mula *</label>
                <input
                  type="time"
                  value={scheduleForm.startTime}
                  onChange={(event) => handleScheduleFormChange('startTime', event.target.value)}
                />
              </div>

              <div className="admin-field schedule-field-end">
                <label>Tamat *</label>
                <input
                  type="time"
                  value={scheduleForm.endTime}
                  onChange={(event) => handleScheduleFormChange('endTime', event.target.value)}
                />
              </div>

              <div className="admin-field schedule-field-school">
                <label>Sekolah/Terminal *</label>
                <input
                  list="admin-school-options"
                  value={scheduleForm.schoolName}
                  onChange={(event) => handleScheduleFormChange('schoolName', event.target.value)}
                  placeholder="SMK Jeli"
                />
                <datalist id="admin-school-options">
                  {manualSchoolNames.map((school) => (
                    <option key={school} value={school} />
                  ))}
                </datalist>
              </div>

              <div className="admin-field schedule-field-district">
                <label>Daerah *</label>
                <input
                  value={scheduleForm.daerah}
                  onChange={(event) => handleScheduleFormChange('daerah', event.target.value)}
                  placeholder="Jeli"
                />
              </div>

              <div className="admin-field schedule-field-subject">
                <label>Subjek / Aktiviti *</label>
                <input
                  list="admin-subject-options"
                  value={scheduleForm.subject}
                  onChange={(event) => handleScheduleFormChange('subject', event.target.value)}
                  placeholder="BM, BI, SEJ, MATH, Play & Talk"
                />
                <datalist id="admin-subject-options">
                  {SUBJECT_ACTIVITY_OPTIONS.map((item) => (
                    <option key={item} value={item} />
                  ))}
                </datalist>
              </div>

              <div className="admin-field schedule-field-teacher">
                <label>Guru / Jurulatih *</label>
                <input
                  value={scheduleForm.teacher}
                  onChange={(event) => handleScheduleFormChange('teacher', event.target.value)}
                  placeholder="Nama cikgu"
                />
              </div>

              <div className="admin-field schedule-field-type">
                <label>Kategori auto</label>
                <select
                  value={scheduleForm.type}
                  onChange={(event) => handleScheduleFormChange('type', event.target.value)}
                >
                  <option value="IVC">IVC</option>
                  <option value="PlayAndTalk">Play & Talk</option>
                  <option value="Workshop">Workshop</option>
                  <option value="Quiz">Quiz</option>
                  <option value="Seminar">Seminar</option>
                </select>
              </div>

              <div className="admin-field schedule-field-room">
                <label>Bilik</label>
                <input
                  value={scheduleForm.room}
                  onChange={(event) => handleScheduleFormChange('room', event.target.value)}
                  placeholder="Pilihan"
                />
              </div>

              <label className="admin-active-toggle">
                <input
                  type="checkbox"
                  checked={scheduleForm.active}
                  onChange={(event) => handleScheduleFormChange('active', event.target.checked)}
                />
                <span>Jadual aktif</span>
              </label>
            </div>

            <div className="admin-editor-actions">
              <button className="admin-editor-save" type="submit" disabled={savingSchedule}>
                {savingSchedule ? 'Menyimpan...' : editingScheduleId ? 'Kemaskini Jadual' : 'Tambah Jadual'}
              </button>
              <button className="admin-editor-reset" type="button" onClick={resetScheduleForm}>
                Reset
              </button>
            </div>
          </form>
          )}

          {scheduleDisplayMode === 'calendar' ? (
            <div className="admin-calendar-board">
              <div className="admin-calendar-toolbar">
                <div>
                  <span>Ringkasan Kalendar</span>
                  <h3>{scheduleCalendarMonthLabel}</h3>
                  <p>{scheduleTimelineRows.length} kelas dipaparkan ikut sekolah terminal</p>
                </div>
                <div className="admin-calendar-actions">
                  <button type="button" onClick={goScheduleCalendarToday}>Hari ini</button>
                  <button type="button" onClick={() => moveScheduleCalendarMonth(-1)} aria-label="Bulan sebelum">&lt;</button>
                  <button type="button" onClick={() => moveScheduleCalendarMonth(1)} aria-label="Bulan seterusnya">&gt;</button>
                </div>
              </div>

              <div className="admin-calendar-layout">
                <div className="admin-calendar-main">
                  <div className="admin-calendar-weekdays">
                    {ADMIN_WEEKDAYS.map((day) => (
                      <div key={day}>{day}</div>
                    ))}
                  </div>

                  <div className="admin-calendar-grid">
                    {scheduleCalendarDays.map((day) => (
                      <button
                        key={day.iso}
                        type="button"
                        className={`admin-calendar-day ${day.isCurrentMonth ? '' : 'muted'} ${day.isToday ? 'today' : ''} ${day.isSelected ? 'selected' : ''}`}
                        onClick={() => setScheduleCalendarDate(day.iso)}
                      >
                        <span className="admin-day-number">{day.date.getDate()}</span>
                        <div className="admin-day-events">
                          {day.rows.slice(0, 3).map((event) => (
                            <span key={event.id} className={`admin-event-chip ${getScheduleTypeClass(event.type)}`}>
                              {event.subject || event.type || 'Kelas'}
                            </span>
                          ))}
                          {day.rows.length > 3 && (
                            <span className="admin-event-more">+{day.rows.length - 3} lagi</span>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                <aside className="admin-calendar-panel">
                  <div className="admin-calendar-panel-head">
                    <span>Tarikh Dipilih</span>
                    <h3>{formatMalayDate(scheduleCalendarDate)}</h3>
                    <p>{selectedCalendarRows.length} kelas / aktiviti</p>
                  </div>

                  {selectedCalendarRows.length === 0 ? (
                    <div className="admin-calendar-empty">
                      <strong>Tiada kelas</strong>
                      <p>Tak ada jadual untuk tarikh ini dalam pilihan semasa.</p>
                    </div>
                  ) : (
                    <div className="admin-calendar-event-list">
                      {selectedCalendarRows.map((row) => (
                        <article key={row.id} className="admin-calendar-event-card">
                          <div className="admin-calendar-event-time">
                            <strong>{row.startTime ? formatDisplayTime(row.startTime) : '-'}</strong>
                            <span>{row.endTime ? formatDisplayTime(row.endTime) : 'Tamat belum ada'}</span>
                          </div>
                          <div className="admin-calendar-event-body">
                            <div className="admin-calendar-event-title">
                              <h4>{row.subject || 'Subjek'}</h4>
                              <span className={`admin-event-type ${getScheduleTypeClass(row.type)}`}>{row.type || 'IVC'}</span>
                            </div>
                            <p>{row.schoolName || 'Sekolah belum ditetapkan'}</p>
                            <p>{row.teacher || 'Cikgu belum ditetapkan'} / {row.daerah || '-'} / {row.terminal || '-'}</p>
                            <div className="admin-calendar-status-row">
                              <span className={`admin-status ${row.reportSubmitted ? 'good' : 'warn'}`}>
                                {row.reportSubmitted ? 'Laporan dihantar' : 'Belum laporan'}
                              </span>
                              <span className={`admin-status ${row.feedbackSubmitted ? 'good' : 'warn'}`}>
                                {row.feedbackSubmitted ? 'Ada maklum balas' : 'Belum maklum balas'}
                              </span>
                            </div>
                            <div className="admin-row-actions admin-calendar-row-actions">
                              <button
                                type="button"
                                onClick={() => handleEditSchedule(row)}
                                disabled={row.source !== 'firestore'}
                              >
                                Kemaskini
                              </button>
                              <button
                                type="button"
                                className="danger"
                                onClick={() => handleDeleteSchedule(row)}
                                disabled={row.source !== 'firestore'}
                              >
                                Padam
                              </button>
                            </div>
                          </div>
                        </article>
                      ))}
                    </div>
                  )}
                </aside>
              </div>
            </div>
          ) : (
          <div className="admin-month-groups">
            {timelineMonthGroups.length === 0 ? (
              <div className="admin-card admin-empty">
                <div className="admin-empty-emoji admin-empty-calendar" aria-hidden="true"><span /></div>
                <h4>Tiada jadual dijumpai</h4>
                <p>Tiada kelas dijumpai untuk pilihan jadual ini.</p>
              </div>
            ) : (
              timelineMonthGroups.map((monthGroup) => (
                <section key={monthGroup.key} className="admin-month-section">
                  <div className="admin-month-divider">
                    <span>{monthGroup.label}</span>
                    <strong>{monthGroup.rows.length} kelas</strong>
                  </div>

          <div className="admin-card admin-table-card admin-today-card">
            <div className="admin-table-wrap">
              <table className="admin-table admin-today-table">
                <thead>
                  <tr>
                    <th>Tarikh</th>
                    <th>Masa</th>
                    <th>Sekolah / Terminal</th>
                    <th>Daerah</th>
                    <th>Terminal</th>
                    <th>Subjek / Aktiviti</th>
                    <th>Guru / Jurulatih</th>
                    <th>Status Laporan</th>
                    <th>Maklum Balas</th>
                    <th>Tindakan</th>
                  </tr>
                </thead>
                <tbody>
                    {monthGroup.rows.map((row) => (
                      <tr key={row.id}>
                        <td>
                          <strong>{formatMalayDate(row.date)}</strong>
                          <span className="admin-table-subtext">
                            {row.date === todayISO
                              ? 'Hari ini'
                              : row.date > todayISO
                              ? 'Akan datang'
                              : 'Lepas'}
                          </span>
                        </td>
                        <td>
                          <strong className="admin-time-strong">
                            {row.startTime ? formatDisplayTime(row.startTime) : '-'}
                          </strong>
                          {row.endTime ? (
                            <span className="admin-time-muted">
                              hingga {formatDisplayTime(row.endTime)}
                            </span>
                          ) : null}
                        </td>
                        <td>{row.schoolName || '-'}</td>
                        <td>{row.daerah || '-'}</td>
                        <td>{row.terminal || '-'}</td>
                        <td>
                          <strong>{row.subject || '-'}</strong>
                          <span className="admin-table-subtext">{row.type || '-'}</span>
                          {row.source === 'static' && (
                            <span className="admin-source-badge">Import asal</span>
                          )}
                        </td>
                        <td>{row.teacher || '-'}</td>
                        <td>
                          <span
                            className={`admin-status ${
                              row.reportSubmitted ? 'good' : 'warn'
                            }`}
                          >
                            {row.reportSubmitted ? 'Laporan dihantar' : 'Belum dihantar'}
                          </span>
                        </td>
                        <td>
                          <span
                            className={`admin-status ${
                              row.feedbackSubmitted ? 'good' : 'warn'
                            }`}
                          >
                            {row.feedbackSubmitted ? 'Ada maklum balas' : 'Belum ada'}
                          </span>
                        </td>
                        <td>
                          <div className="admin-row-actions">
                            <button
                              type="button"
                              onClick={() => handleEditSchedule(row)}
                              disabled={row.source !== 'firestore'}
                            >
                              Kemaskini
                            </button>
                            <button
                              type="button"
                              className="danger"
                              onClick={() => handleDeleteSchedule(row)}
                              disabled={row.source !== 'firestore'}
                            >
                              Padam
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
                </section>
              ))
            )}
          </div>
          )}
        </section>
        )}

        {activeAdminView === 'performance' && (
        <section className="admin-section admin-performance-section">
          <div className="admin-section-head">
            <div className="admin-section-titleblock">
              <h3>Rekod Prestasi Pelajar</h3>
              <div className="admin-view-meta">
                Masukkan baseline dan rekod terkini untuk 4 subjek fokus: BM, BI, MATH dan SEJARAH.
              </div>
            </div>
            <div className="admin-section-actions">
              <span className="admin-subpill">{studentsList.length} pelajar</span>
              <button className="admin-export-btn" type="button" onClick={handleDownloadPerformanceTemplate}>
                Muat Turun Templat
              </button>
              <label className={`admin-export-btn admin-import-btn ${importingPerformance ? 'disabled' : ''}`}>
                {importingPerformance ? 'Sedang Import...' : 'Import Prestasi'}
                <input type="file" accept=".csv,text/csv" onChange={handleImportPerformance} disabled={importingPerformance} />
              </label>
            </div>
          </div>

          <div className="admin-performance-board">
            <div className="admin-performance-panel admin-performance-summary">
              <div>
                <span>Graf Prestasi</span>
                <h4>{selectedPerformanceStudent?.fullName || 'Pilih pelajar untuk paparan'}</h4>
                <select
                  className="admin-performance-select"
                  value={performanceForm.studentId}
                  onChange={(event) => handlePerformanceFormChange('studentId', event.target.value)}
                >
                  <option value="">Pilih pelajar</option>
                  {studentsList.map((student) => (
                    <option key={student.uid} value={student.uid}>
                      {student.fullName || student.email || student.uid}
                    </option>
                  ))}
                </select>
                <p>
                  {performanceForm.studentId
                    ? `${adminPerformanceSummary.withRecord}/4 subjek ada rekod. ${adminPerformanceSummary.achieved}/4 capai sasaran naik 2 gred.`
                    : 'Pilih pelajar dalam borang untuk papar graf BM, BI, MATH dan SEJARAH.'}
                </p>
              </div>

              <div
                className="admin-performance-ring"
                style={{ '--progress': `${adminPerformanceSummary.progressPercent}%` }}
              >
                <div>
                  <strong>{loadingPerformanceRecords ? '...' : `${adminPerformanceSummary.progressPercent}%`}</strong>
                  <span>sasaran</span>
                </div>
              </div>

              <div className="admin-performance-kpis">
                <div>
                  <strong>{loadingPerformanceRecords ? '...' : `${adminPerformanceSummary.achieved}/4`}</strong>
                  <small>Subjek capai sasaran</small>
                </div>
                <div>
                  <strong>{loadingPerformanceRecords ? '...' : (adminPerformanceSummary.averageMark ?? '-')}</strong>
                  <small>Purata markah</small>
                </div>
              </div>
            </div>

            <div className="admin-performance-chart">
              {loadingPerformanceRecords ? (
                <div className="admin-empty-inline">Memuatkan graf prestasi...</div>
              ) : (
                adminPerformanceBySubject.map((item) => (
                  <article
                    key={item.subject}
                    className={`admin-performance-row ${item.achieved ? 'achieved' : ''}`}
                    style={{
                      '--baseline': `${item.baselineMark ?? 0}%`,
                      '--current': `${item.markProgress}%`,
                    }}
                  >
                    <div className="admin-performance-row-head">
                      <div>
                        <strong>{item.subject}</strong>
                        <span>{item.hasRecord ? item.examLabel || 'Rekod semasa' : 'Belum ada rekod'}</span>
                      </div>
                      <em className={item.achieved ? 'ok' : item.hasRecord ? 'watch' : ''}>
                        {item.achieved ? 'Capai' : item.hasRecord ? 'Pantau' : 'Kosong'}
                      </em>
                    </div>

                    <div className="admin-performance-visual">
                      <div className="admin-performance-score">
                        <strong>{item.currentMark ?? '-'}</strong>
                        <span>markah</span>
                      </div>

                      <div className="admin-performance-mini-chart" aria-hidden="true">
                        <i className="baseline" />
                        <i className={item.achieved ? 'current achieved' : 'current'} />
                        <span />
                      </div>
                    </div>

                    <div className="admin-performance-bars">
                      <div className="admin-performance-barline">
                        <small>Baseline {item.baselineGrade}</small>
                        <div><i className="baseline" /></div>
                        <b>{item.baselineMark ?? '-'}</b>
                      </div>

                      <div className="admin-performance-barline">
                        <small>Terkini {item.currentGrade}</small>
                        <div><i className={item.achieved ? 'current achieved' : 'current'} /></div>
                        <b>{item.currentMark ?? '-'}</b>
                      </div>
                    </div>

                    <footer>
                      <span>Sasaran {item.targetGrade}</span>
                      {item.markDelta === null ? (
                        <strong>Menunggu input</strong>
                      ) : (
                        <strong className={item.markDelta >= 0 ? 'up' : 'down'}>
                          {item.markDelta > 0 ? `+${item.markDelta}` : item.markDelta} markah
                        </strong>
                      )}
                    </footer>
                  </article>
                ))
              )}
            </div>
          </div>

          <form className="admin-schedule-editor" onSubmit={handleSavePerformance}>
            <div className="admin-editor-grid performance-editor-grid">
              <div className="admin-field">
                <label>Pelajar *</label>
                <select
                  value={performanceForm.studentId}
                  onChange={(event) => handlePerformanceFormChange('studentId', event.target.value)}
                >
                  <option value="">Pilih pelajar</option>
                  {studentsList.map((student) => (
                    <option key={student.uid} value={student.uid}>
                      {student.fullName || student.email || student.uid}
                    </option>
                  ))}
                </select>
              </div>

              <div className="admin-field">
                <label>Subjek *</label>
                <select
                  value={performanceForm.subject}
                  onChange={(event) => handlePerformanceFormChange('subject', event.target.value)}
                >
                  {PERFORMANCE_SUBJECTS.map((subject) => (
                    <option key={subject} value={subject}>{subject}</option>
                  ))}
                </select>
              </div>

              <div className="admin-field">
                <label>Baseline Gred</label>
                <select
                  value={performanceForm.baselineGrade}
                  onChange={(event) => handlePerformanceFormChange('baselineGrade', event.target.value)}
                >
                  {PERFORMANCE_GRADES.map((grade) => (
                    <option key={grade} value={grade}>{grade}</option>
                  ))}
                </select>
              </div>

              <div className="admin-field">
                <label>Baseline Markah</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={performanceForm.baselineMark}
                  onChange={(event) => handlePerformanceFormChange('baselineMark', event.target.value)}
                  placeholder="Contoh: 45"
                />
              </div>

              <div className="admin-field">
                <label>Gred Terkini</label>
                <select
                  value={performanceForm.currentGrade}
                  onChange={(event) => handlePerformanceFormChange('currentGrade', event.target.value)}
                >
                  {PERFORMANCE_GRADES.map((grade) => (
                    <option key={grade} value={grade}>{grade}</option>
                  ))}
                </select>
              </div>

              <div className="admin-field">
                <label>Markah Terkini</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={performanceForm.currentMark}
                  onChange={(event) => handlePerformanceFormChange('currentMark', event.target.value)}
                  placeholder="Contoh: 68"
                />
              </div>

              <div className="admin-field">
                <label>Nama Ujian / Exam</label>
                <input
                  value={performanceForm.examLabel}
                  onChange={(event) => handlePerformanceFormChange('examLabel', event.target.value)}
                  placeholder="Contoh: Ujian 1 / PPT"
                />
              </div>
            </div>

            <div className="admin-editor-actions">
              <button className="admin-editor-save" type="submit" disabled={savingPerformance}>
                {savingPerformance ? 'Menyimpan...' : 'Simpan Prestasi'}
              </button>
              <button
                className="admin-editor-reset"
                type="button"
                onClick={() => setPerformanceForm(EMPTY_PERFORMANCE_FORM)}
              >
                Reset
              </button>
            </div>
          </form>

        </section>
        )}

        {activeAdminView === 'feedback' && (
        <section className="admin-section admin-reflection-section">
          <div className="admin-section-head">
            <div className="admin-section-titleblock">
              <h3>Refleksi Guru Dari Google Form</h3>
              <div className="admin-view-meta">
                Visualisasi refleksi keseluruhan yang dihantar oleh guru melalui Google Form.
              </div>
            </div>
            <span className="admin-subpill">{filteredTeacherReflections.length} rekod</span>
          </div>

          <div className="admin-reflection-filterbar">
            <div className="admin-schedule-filter">
              <label>Tarikh</label>
              <select
                value={reflectionDateFilter}
                onChange={(event) => setReflectionDateFilter(event.target.value)}
              >
                <option value="">Semua tarikh</option>
                {reflectionFilterOptions.dates.map((date) => (
                  <option key={date} value={date}>{formatMalayDate(date)}</option>
                ))}
              </select>
            </div>

            <div className="admin-schedule-filter">
              <label>Subjek</label>
              <select
                value={reflectionSubjectFilter}
                onChange={(event) => setReflectionSubjectFilter(event.target.value)}
              >
                <option value="">Semua subjek</option>
                {reflectionFilterOptions.subjects.map((subject) => (
                  <option key={subject} value={subject}>{subject}</option>
                ))}
              </select>
            </div>

            <div className="admin-schedule-filter">
              <label>Daerah</label>
              <select
                value={reflectionDistrictFilter}
                onChange={(event) => {
                  setReflectionDistrictFilter(event.target.value);
                  setReflectionSchoolFilter('');
                }}
              >
                <option value="">Semua daerah</option>
                {reflectionFilterOptions.districts.map((district) => (
                  <option key={district} value={district}>{district}</option>
                ))}
              </select>
            </div>

            <div className="admin-schedule-filter">
              <label>Terminal</label>
              <select
                value={reflectionTerminalFilter}
                onChange={(event) => {
                  setReflectionTerminalFilter(event.target.value);
                  setReflectionSchoolFilter('');
                }}
              >
                <option value="">Semua terminal</option>
                {reflectionFilterOptions.terminals.map((terminal) => (
                  <option key={terminal} value={terminal}>{terminal}</option>
                ))}
              </select>
            </div>

            <div className="admin-schedule-filter">
              <label>Sekolah</label>
              <select
                value={reflectionSchoolFilter}
                onChange={(event) => setReflectionSchoolFilter(event.target.value)}
              >
                <option value="">{selectedSchool ? `Ikut ${selectedSchool}` : 'Semua sekolah'}</option>
                {reflectionFilterOptions.schools
                  .filter((school) => {
                    const meta = getManualSchoolMeta(school);
                    const matchDistrict = reflectionDistrictFilter ? meta.daerah === reflectionDistrictFilter : true;
                    const matchTerminal = reflectionTerminalFilter ? meta.terminal === reflectionTerminalFilter : true;
                    return matchDistrict && matchTerminal;
                  })
                  .map((school) => (
                    <option key={school} value={school}>{school}</option>
                  ))}
              </select>
            </div>

            {activeReflectionFilterCount > 0 && (
              <button
                className="admin-schedule-filter-reset"
                type="button"
                onClick={resetReflectionFilters}
              >
                Reset Penapis
              </button>
            )}
          </div>

          <div className="admin-feedback-summary">
            <div className="admin-feedback-mini">
              <span>Jumlah refleksi</span>
              <strong>{loadingSheet ? '...' : teacherReflectionSummary.total}</strong>
            </div>
            <div className="admin-feedback-mini">
              <span>Objektif tercapai</span>
              <strong>{loadingSheet ? '...' : `${teacherReflectionRate}%`}</strong>
            </div>
            <div className="admin-feedback-mini">
              <span>Ada cabaran</span>
              <strong>{loadingSheet ? '...' : teacherReflectionSummary.withChallenge}</strong>
            </div>
          </div>

          {!loadingSheet && reflectionLikertSummary.some((metric) => metric.total > 0) && (
            <div className="admin-reflection-charts">
              {reflectionLikertSummary.map((metric) => (
                <article key={metric.key} className="admin-reflection-chart-card">
                  <div className="admin-reflection-chart-head">
                    <div>
                      <span>{metric.label}</span>
                      <strong>{metric.averageLabel}</strong>
                    </div>
                    <em>{metric.total} respon</em>
                  </div>
                  <div className="admin-reflection-scale">
                    <small>{metric.low}</small>
                    <small>{metric.high}</small>
                  </div>
                  <div className="admin-reflection-bars">
                    {[1, 2, 3, 4, 5].map((score) => {
                      const count = metric.counts[score] || 0;
                      const percent = metric.total ? Math.round((count / metric.total) * 100) : 0;
                      return (
                        <div key={score} className="admin-reflection-bar-row">
                          <b>{score}</b>
                          <div>
                            <i style={{ width: `${percent}%` }} />
                          </div>
                          <span>{count}</span>
                        </div>
                      );
                    })}
                  </div>
                </article>
              ))}
            </div>
          )}

          {loadingSheet ? (
            <div className="admin-card admin-empty-inline">
              Memuatkan refleksi guru daripada Google Sheet...
            </div>
          ) : filteredTeacherReflections.length === 0 ? (
            <div className="admin-card admin-empty">
              <div className="admin-empty-emoji">NOTE</div>
              <h4>Belum ada refleksi guru</h4>
              <p>Refleksi daripada Google Form akan dipaparkan di sini selepas Google Sheet berjaya diselaraskan.</p>
            </div>
          ) : (
            <div className="admin-feedback-list">
              {filteredTeacherReflections.slice(0, 18).map((reflection) => (
                <article key={reflection.id} className="admin-feedback-card">
                  <div className="admin-feedback-head">
                    <div>
                      <h4>{reflection.subject || 'Subjek'}</h4>
                      <p>
                        {reflection.teacher || 'Guru'} / {formatMalayDate(reflection.date)}
                      </p>
                    </div>
                    <div className="admin-reflection-score">
                      <strong>{reflection.percent || 0}%</strong>
                      <span>hadir</span>
                    </div>
                  </div>

                  <div className="admin-feedback-meta">
                    <span>{reflection.schoolName || 'Sekolah tidak dinyatakan'}</span>
                    <span>{reflection.daerah || '-'}</span>
                    <span>{reflection.terminal || '-'}</span>
                    <span>{reflection.session || '-'}</span>
                    <span className={(parseLikertScore(reflection.objective) >= 4 || /ya|yes|tercapai|capai/i.test(String(reflection.objective || ''))) ? 'is-good' : 'is-warn'}>
                      {(parseLikertScore(reflection.objective) >= 4 || /ya|yes|tercapai|capai/i.test(String(reflection.objective || ''))) ? 'Objektif tercapai' : 'Perlu semak'}
                    </span>
                  </div>

                  <div className="admin-reflection-grid">
                    <div>
                      <span>Penguasaan</span>
                      <strong>{parseLikertScore(reflection.mastery) || '-'}</strong>
                      <p>{reflection.mastery || 'Tiada catatan'}</p>
                    </div>
                    <div>
                      <span>Penglibatan</span>
                      <strong>{parseLikertScore(reflection.engagement) || '-'}</strong>
                      <p>{reflection.engagement || 'Tiada catatan'}</p>
                    </div>
                    <div>
                      <span>Objektif</span>
                      <strong>{parseLikertScore(reflection.objective) || '-'}</strong>
                      <p>{reflection.objective || 'Tiada catatan'}</p>
                    </div>
                  </div>

                  <div className="admin-reflection-notes">
                    <div>
                      <span>Cabaran</span>
                      <p>{reflection.challenge || 'Tiada cabaran dicatat.'}</p>
                    </div>
                    <div>
                      <span>Pemerhatian</span>
                      <p>{reflection.observation || 'Tiada pemerhatian tambahan.'}</p>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
        )}

        {activeAdminView === 'reports' && (
        <div className="admin-reports-view">
        <section className="admin-section">
          <div className="admin-section-head">
            <h3>Sekolah Belum Hantar Laporan</h3>
            <span className="admin-subpill">{pendingReportSchools.length} sekolah</span>
          </div>

          <div className="admin-card">
            {pendingReportSchools.length === 0 ? (
              <div className="admin-empty-inline">
                Semua sekolah untuk tarikh ini sudah menghantar laporan.
              </div>
            ) : (
              <div className="admin-critical-list">
                {pendingReportSchools.map((school) => (
                  <div key={school} className="admin-critical-item">
                    <div>
                      <div className="admin-critical-name">{school}</div>
                      <div className="admin-critical-meta">
                        Ada jadual pada {formatMalayDate(selectedDate)} tetapi belum ada laporan kehadiran.
                      </div>
                    </div>

                    <div className="admin-critical-right">
                      <div className="admin-critical-percent">TUNGGU</div>
                      <div className="admin-critical-status">Belum Hantar</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        <section className="admin-section">
          <div className="admin-section-head">
            <div className="admin-section-titleblock">
              <h3>Peringatan Kehadiran Guru</h3>
              <div className="admin-view-meta">
                Kelas dalam 14 hari terkini yang sudah tamat tetapi belum mempunyai rekod kehadiran dalam Google Sheet.
              </div>
            </div>
            <span className="admin-subpill">{filteredTeacherAttendanceReminderRows.length} / {teacherAttendanceReminderRows.length} sesi</span>
          </div>

          <div className="admin-card admin-reminder-card">
            <div className="admin-reminder-summary">
              <button type="button" className="admin-reminder-stat today" onClick={() => setReminderPriorityFilter('today')}>
                <span>Hari ini</span>
                <strong>{teacherAttendanceReminderSummary.today}</strong>
              </button>
              <button type="button" className="admin-reminder-stat soon" onClick={() => setReminderPriorityFilter('soon')}>
                <span>Lewat 1-2 hari</span>
                <strong>{teacherAttendanceReminderSummary.soon}</strong>
              </button>
              <button type="button" className="admin-reminder-stat late" onClick={() => setReminderPriorityFilter('late')}>
                <span>Lewat 3-14 hari</span>
                <strong>{teacherAttendanceReminderSummary.late}</strong>
              </button>
              <button type="button" className="admin-reminder-stat done" onClick={() => setReminderPriorityFilter('done')}>
                <span>Sudah dihubungi</span>
                <strong>{teacherAttendanceReminderSummary.done}</strong>
              </button>
            </div>

            <div className="admin-reminder-toolbar">
              <label>
                <span>Cari</span>
                <input
                  type="search"
                  value={reminderSearch}
                  onChange={(event) => setReminderSearch(event.target.value)}
                  placeholder="Nama cikgu, sekolah atau subjek"
                />
              </label>

              <label>
                <span>Tarikh</span>
                <select value={reminderDateFilter} onChange={(event) => setReminderDateFilter(event.target.value)}>
                  <option value="">Semua tarikh</option>
                  {teacherAttendanceReminderOptions.dates.map((date) => (
                    <option key={date} value={date}>{formatMalayDate(date)}</option>
                  ))}
                </select>
              </label>

              <label>
                <span>Subjek</span>
                <select value={reminderSubjectFilter} onChange={(event) => setReminderSubjectFilter(event.target.value)}>
                  <option value="">Semua subjek</option>
                  {teacherAttendanceReminderOptions.subjects.map((subject) => (
                    <option key={subject} value={subject}>{subject}</option>
                  ))}
                </select>
              </label>

              <label>
                <span>Daerah</span>
                <select value={reminderDistrictFilter} onChange={(event) => setReminderDistrictFilter(event.target.value)}>
                  <option value="">Semua daerah</option>
                  {teacherAttendanceReminderOptions.districts.map((district) => (
                    <option key={district} value={district}>{district}</option>
                  ))}
                </select>
              </label>

              <label>
                <span>Terminal</span>
                <select value={reminderTerminalFilter} onChange={(event) => setReminderTerminalFilter(event.target.value)}>
                  <option value="">Semua terminal</option>
                  {teacherAttendanceReminderOptions.terminals.map((terminal) => (
                    <option key={terminal} value={terminal}>{terminal}</option>
                  ))}
                </select>
              </label>

              <label>
                <span>Status</span>
                <select value={reminderPriorityFilter} onChange={(event) => setReminderPriorityFilter(event.target.value)}>
                  <option value="">Semua status</option>
                  <option value="today">Hari ini</option>
                  <option value="soon">Lewat 1-2 hari</option>
                  <option value="late">Lewat 3-14 hari</option>
                  <option value="done">Sudah dihubungi</option>
                </select>
              </label>

              {(reminderSearch || reminderDateFilter || reminderSubjectFilter || reminderDistrictFilter || reminderTerminalFilter || reminderPriorityFilter) && (
                <button
                  type="button"
                  className="admin-reminder-reset"
                  onClick={() => {
                    setReminderSearch('');
                    setReminderDateFilter('');
                    setReminderSubjectFilter('');
                    setReminderDistrictFilter('');
                    setReminderTerminalFilter('');
                    setReminderPriorityFilter('');
                  }}
                >
                  Reset Penapis
                </button>
              )}
            </div>

            {teacherAttendanceReminderRows.length === 0 ? (
              <div className="admin-empty-inline">
                Tiada peringatan buat masa ini. Semua kelas yang sudah tamat mempunyai rekod kehadiran.
              </div>
            ) : filteredTeacherAttendanceReminderRows.length === 0 ? (
              <div className="admin-empty-inline">
                Tiada kelas dijumpai untuk penapis yang dipilih.
              </div>
            ) : (
              <div className="admin-reminder-list">
                {filteredTeacherAttendanceReminderRows.slice(0, 8).map((row) => (
                  <article key={`${row.id}-${row.date}-${row.subject}`} className={`admin-reminder-item ${row.followupDone ? 'done' : row.reminderPriorityTone || 'today'}`}>
                    <div className="admin-reminder-main">
                      <div className="admin-reminder-badges">
                        <span className="admin-reminder-date">{formatMalayDate(row.date)}</span>
                        <span className={`admin-reminder-priority ${row.followupDone ? 'done' : row.reminderPriorityTone || 'today'}`}>
                          {row.followupDone ? 'Sudah dihubungi' : row.reminderPriorityLabel}
                        </span>
                      </div>
                      <h4>{row.subject || 'Subjek tidak dinyatakan'}</h4>
                      <p>
                        {row.schoolName || 'Sekolah tidak dinyatakan'} / {row.daerah || '-'} / {row.terminal || '-'}
                      </p>
                      {row.schoolCount > 1 && <small className="admin-reminder-school-count">{row.schoolCount} sekolah dalam sesi ini</small>}
                    </div>

                    <div className="admin-reminder-side">
                      <strong>{row.teacher || 'Cikgu tidak dinyatakan'}</strong>
                      <small>
                        {row.startTime ? formatDisplayTime(row.startTime) : '-'}
                        {row.endTime ? ` - ${formatDisplayTime(row.endTime)}` : ''}
                      </small>
                      <em>{row.followupDone ? 'Menunggu rekod kehadiran' : 'Belum rekod kehadiran'}</em>
                      <button type="button" onClick={() => handleOpenReminderWhatsApp(row)} disabled={!getTeacherPhone(row)}>
                        {getTeacherPhone(row) ? 'Buka WhatsApp' : 'Nombor belum ada'}
                      </button>
                      <button type="button" onClick={() => handleCopyReminderMessage(row)}>
                        Salin mesej
                      </button>
                      <button
                        type="button"
                        className={row.followupDone ? 'secondary' : ''}
                        onClick={() => handleToggleReminderFollowup(row, !row.followupDone)}
                      >
                        {row.followupDone ? 'Buka semula' : 'Sudah dihubungi'}
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            )}

            {filteredTeacherAttendanceReminderRows.length > 8 && (
              <div className="admin-reminder-more">
                +{filteredTeacherAttendanceReminderRows.length - 8} sesi lagi. Gunakan penapis untuk semakan khusus.
              </div>
            )}
          </div>
        </section>

        <section className="admin-section">
          <div className="admin-section-head admin-section-head-schedule">
            <div className="admin-section-titleblock">
              <h3>Jadual Ikut Sekolah</h3>
              <div className="admin-view-meta">
                {visibleSchoolsCount} sekolah / {visibleClassesCount} kelas dipaparkan
              </div>
            </div>

            <div className="admin-view-toggle">
              <button
                type="button"
                onClick={() => setScheduleViewMode('all')}
                className={`admin-view-toggle-btn ${scheduleViewMode === 'all' ? 'active all' : ''}`}
              >
                Semua sekolah
              </button>

              <button
                type="button"
                onClick={() => setScheduleViewMode('pending')}
                className={`admin-view-toggle-btn ${scheduleViewMode === 'pending' ? 'active pending' : ''}`}
              >
                Belum hantar sahaja
              </button>
            </div>
          </div>

          {Object.keys(visibleGroupedSchedules).length === 0 ? (
            <div className="admin-card admin-empty">
              <div className="admin-empty-emoji admin-empty-calendar" aria-hidden="true"><span /></div>
              <h4>Tiada jadual dijumpai</h4>
              <p>
                {scheduleViewMode === 'pending'
                  ? 'Tiada sekolah yang belum hantar laporan untuk penapis ini.'
                  : 'Tiada rekod jadual untuk penapis yang dipilih.'}
              </p>
            </div>
          ) : (
            <div className="admin-school-groups">
              {Object.entries(visibleGroupedSchedules).map(([schoolName, schoolRows]) => {
                const isPending = pendingReportSchools.includes(schoolName);

                return (
                  <div
                    key={schoolName}
                    className={`admin-school-card ${isPending ? 'pending' : ''}`}
                  >
                    <div className="admin-school-head">
                      <div className="admin-school-title-wrap">
                        <h4>{schoolName}</h4>
                        {isPending && (
                          <span className="admin-pending-badge">
                            TUNGGU Belum Hantar Laporan
                          </span>
                        )}
                      </div>
                      <span>{schoolRows.length} kelas</span>
                    </div>

                    <div className="admin-schedule-list">
                      {schoolRows.map((row) => (
                        <div key={row.id} className="admin-schedule-item">
                          <div className="admin-schedule-time">
                            {row.startTime ? formatDisplayTime(row.startTime) : '-'}
                            {row.endTime ? ` - ${formatDisplayTime(row.endTime)}` : ''}
                          </div>

                          <div className="admin-schedule-main">
                            <div className="admin-schedule-subject">
                              {row.subject || '-'}
                            </div>

                            <div className="admin-schedule-meta">
                              <span>Cikgu {row.teacher || '-'}</span>
                              <span>{row.terminal || '-'}</span>
                              <span>{row.daerah || '-'}</span>
                              <span>NOTE {row.type || '-'}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        <section className="admin-section">
          <div className="admin-section-head">
            <div className="admin-section-titleblock">
              <h3>Kehadiran Mengikut Tarikh, Sekolah & Subjek</h3>
              <div className="admin-view-meta">
                Data ini dibaca daripada CSV Google Sheet dan dikumpulkan mengikut tarikh supaya rekod sesi tidak bercampur.
              </div>
            </div>
            <div className="admin-section-actions">
              <span className="admin-subpill">{attendanceSubjectMatrix.length} rekod</span>
              {attendanceSubjectMatrix.length > 8 && (
                <button className="admin-export-btn" type="button" onClick={() => setShowAllAttendanceMatrix((current) => !current)}>
                  {showAllAttendanceMatrix ? 'Tunjuk Ringkas' : 'Lihat Semua'}
                </button>
              )}
              <button className="admin-export-btn" type="button" onClick={handleExportAttendance}>
                Muat Turun CSV
              </button>
            </div>
          </div>

          <div className="admin-card admin-table-card">
            <div className="admin-table-wrap">
              <table className="admin-table admin-attendance-matrix">
                <thead>
                  <tr>
                    <th>Tarikh</th>
                    <th>Sekolah / Lokasi</th>
                    {PERFORMANCE_SUBJECTS.map((subject) => (
                      <th key={subject}>{subject}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loadingSheet ? (
                    <tr>
                      <td colSpan={6} className="admin-no-data">
                        Memuatkan data CSV...
                      </td>
                    </tr>
                  ) : attendanceSubjectMatrix.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="admin-no-data">
                        Tiada data kehadiran subjek yang boleh dipaparkan.
                      </td>
                    </tr>
                  ) : (
                    (showAllAttendanceMatrix ? attendanceSubjectMatrix : attendanceSubjectMatrix.slice(0, 8)).map((row) => (
                      <tr key={row.key}>
                        <td>
                          <strong>{formatMalayDate(row.date)}</strong>
                          <span className="admin-table-subtext">{row.date}</span>
                        </td>
                        <td>
                          <strong>{row.schoolName}</strong>
                          <span className="admin-table-subtext">{row.daerah || '-'} / {row.terminal || '-'}</span>
                        </td>
                        {PERFORMANCE_SUBJECTS.map((subject) => {
                          const cell = row.subjects[subject];
                          const hasData = cell?.percent !== null;
                          return (
                            <td key={subject}>
                              {hasData ? (
                                <span className={`admin-attendance-cell ${getAttendanceTone(cell.percent)}`}>
                                  <strong>{cell.percent}%</strong>
                                  <small>
                                    {cell.present}/{cell.total}
                                  </small>
                                </span>
                              ) : (
                                <span className="admin-attendance-cell empty">-</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section className="admin-section">
          <div className="admin-section-head">
            <h3>Prestasi Kehadiran Sekolah</h3>
            <button className="admin-export-btn" type="button" onClick={handleExportSchoolPerformance}>
              Muat Turun CSV
            </button>
          </div>

          <div className="admin-card admin-table-card">
            <div className="admin-table-wrap">
              <table className="admin-table admin-school-performance-table">
                <thead>
                  <tr>
                    <th>Sekolah</th>
                    <th>Daerah</th>
                    <th>Terminal</th>
                    <th>Kehadiran Purata</th>
                    <th>% Kehadiran</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingSheet ? (
                    <tr>
                      <td colSpan="6" className="admin-no-data">
                        Memuatkan data kehadiran...
                      </td>
                    </tr>
                  ) : schoolPerformance.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="admin-no-data">
                        Tiada data sekolah untuk dipaparkan.
                      </td>
                    </tr>
                  ) : (
                    schoolPerformance.map((school) => (
                      <tr key={school.schoolName}>
                        <td>{school.schoolName}</td>
                        <td>{school.daerah}</td>
                        <td>{school.terminal}</td>
                        <td>
                          <span className="admin-school-average">
                            <strong>{Math.round(school.averagePresent)}</strong>
                            <small> daripada {Math.round(school.averageStudents)} pelajar</small>
                          </span>
                        </td>
                        <td>{school.percent}%</td>
                        <td>
                          <span
                            className={`admin-status ${
                              school.percent >= KPI_TARGET
                                ? 'good'
                                : school.percent >= 75
                                ? 'warn'
                                : 'bad'
                            }`}
                          >
                            {school.status}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section className="admin-section">
          <div className="admin-section-head">
            <h3>Sekolah Kritikal</h3>
            <span className="admin-subpill">Top 10 kehadiran terendah</span>
          </div>

          <div className="admin-card">
            {loadingSheet ? (
              <div className="admin-empty-inline">
                Memuatkan data kehadiran...
              </div>
            ) : criticalSchools.length === 0 ? (
              <div className="admin-empty-inline">
                Semua sekolah sedang capai KPI.
              </div>
            ) : (
              <div className="admin-critical-list">
                {criticalSchools.map((school) => (
                  <div key={school.schoolName} className="admin-critical-item">
                    <div>
                      <div className="admin-critical-name">{school.schoolName}</div>
                      <div className="admin-critical-meta">
                        {school.daerah} / {school.terminal}
                      </div>
                    </div>

                    <div className="admin-critical-right">
                      <div className="admin-critical-percent">{school.percent}%</div>
                      <div className="admin-critical-status">{school.status}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
        </div>
        )}
      </main>
    </div>
    </DashboardShell>
  );
};

const getScheduleSchoolName = (schedule) => {
  const direct = normalizeSchoolName(schedule?.schoolName);
  if (!isMissingSchoolName(direct)) return direct;

  const sourceId = String(schedule?.id || '').replace(/-/g, ' ');
  const knownSchools = Object.keys(DAERAH_BY_SCHOOL).sort((a, b) => b.length - a.length);
  const matchedSchool = knownSchools.find((school) => sourceId.includes(school));
  return matchedSchool ? normalizeSchoolName(matchedSchool) : 'Sekolah belum ditetapkan';
};

const inferActivityType = (subject) => {
  const value = String(subject || '').toLowerCase();
  if (value.includes('play')) return 'PlayAndTalk';
  if (value.includes('workshop') || value.includes('bengkel')) return 'Workshop';
  if (value.includes('seminar')) return 'Seminar';
  if (value.includes('quiz') || value.includes('kuiz')) return 'Quiz';
  return 'IVC';
};

export default AdminDashboard;
